import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { htmlToPlainText } from '@/lib/stripHtml';
import { formatNacelleDisplay } from '@/lib/formatNacelle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { API_URL } from './utils';
import { useBtDetailActions } from '@/hooks/useBtDetailActions';  // 🧱 Refactor BT Phase 3 — putBon unique
import { calculerStatutOuverture, regrouperHeures } from '@/utils/heuresOuverture';
import { Plus, ClipboardList, Home, TreePine, Trash2, Package, User, MapPin, ChevronDown, ChevronUp, Upload, Image as ImageIcon, CheckCircle, X, Calendar, ChevronLeft, ChevronRight, Clock, LogIn, LogOut as LogOutIcon, Edit, Edit3, Phone, Mail, Building2, Printer, ShoppingCart, History, Send, Wrench, AlertTriangle, AlertCircle, Users, Play, Square, Coffee, Check, MoreVertical, RefreshCw, Cloud, CheckCircle2, MessageSquare, FileText, UploadCloud, Download, Lock } from 'lucide-react';
import { CreerServiceCallDialog } from '../CreerServiceCallDialog';
import { OngletRapportIncident } from '../OngletRapportIncident';
import { useEmailPreview, EmailPreviewFrame } from '../../EmailPreview';
import { SectionInspection } from './SectionInspection';
import { SectionGestionContrat } from './SectionGestionContrat';
import { BtnCommanderPeinture } from './BtnCommanderPeinture';
import { BtnTravauxTermines } from './BtnTravauxTermines';
import { BtnDecocherTravauxTermines } from './BtnDecocherTravauxTermines';
import { EditerBonTravailDialog } from './EditerBonTravailDialog';
import { HeuresBannerBon } from './HeuresBannerBon';
import { urlFichier } from '@/lib/fichiers';

// ─── Helper : sélecteur générique Fournisseur + Magasins ────────────
// 🆕 [2026-06-11 v2] Affiche la liste des fournisseurs actifs. Si le fournisseur
// sélectionné a des magasins, affiche un sous-sélecteur. Sinon l'adresse
// principale du fournisseur est utilisée pour le ramassage.
function FournisseurRamassagePicker({ fournisseurs, fournisseurId, setFournisseurId, magasinId, setMagasinId, quand, setQuand, testIdSuffix = '' }) {
  const f = fournisseurs.find(fr => fr.id === fournisseurId);
  const magasins = f?.magasins || [];
  // 🆕 [2026-06-11 v4] Récupère les heures d'ouverture de la sélection courante
  // (magasin si défini, sinon adresse principale du fournisseur).
  const heuresSelection = (magasinId
    ? (magasins.find(mg => mg.id === magasinId) || {}).heures_ouverture
    : f?.heures_ouverture) || null;
  const statut = useMemo(
    () => calculerStatutOuverture(heuresSelection),
    [heuresSelection]
  );
  const groupesHeures = useMemo(
    () => regrouperHeures(heuresSelection),
    [heuresSelection]
  );
  const badgeStyles = {
    green: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    amber: 'bg-amber-100 text-amber-900 border-amber-400',
    red:   'bg-rose-100 text-rose-800 border-rose-300',
    gray:  'bg-slate-100 text-slate-600 border-slate-300',
  };
  // Sélection complète = fournisseur + (magasin si nécessaire)
  const selectionComplete = !!(f && (magasins.length === 0 || magasinId));
  // 🆕 [2026-06-11 v5] Warning : si "Aujourd'hui" mais magasin actuellement fermé
  const showWarningFerme = selectionComplete && quand === 'aujourd_hui' && statut.disponible && !statut.ouvert;
  return (
    <div className="mt-3 space-y-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`fournisseur-ramassage-selector${testIdSuffix}`}>
      <div>
        <p className="text-sm font-semibold text-amber-900 mb-2">Chez quel fournisseur ?</p>
        {fournisseurs.length === 0 ? (
          <p className="text-xs text-amber-800 italic px-2 py-3 bg-white border border-dashed border-amber-300 rounded">
            Aucun fournisseur disponible. Ajoutez-en un (avec adresse) via la page <strong>Gestion des Produits</strong>.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fournisseurs.map(fr => (
              <button
                key={fr.id}
                type="button"
                onClick={() => setFournisseurId(fr.id)}
                data-testid={`btn-fournisseur-${fr.id}${testIdSuffix}`}
                className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition ${fournisseurId === fr.id ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
              >
                <div className="font-medium">{fr.nom}</div>
                {(fr.magasins || []).length > 0 ? (
                  <div className={`text-[11px] ${fournisseurId === fr.id ? 'text-amber-100' : 'text-slate-500'}`}>{(fr.magasins || []).length} succursale{(fr.magasins || []).length > 1 ? 's' : ''}</div>
                ) : (
                  <div className={`text-[11px] ${fournisseurId === fr.id ? 'text-amber-100' : 'text-slate-500'}`}>{fr.adresse || '(adresse à compléter)'}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Sous-sélecteur magasins si le fournisseur en a */}
      {f && magasins.length > 0 && (
        <div className="border-t border-amber-200 pt-3" data-testid={`magasin-selector${testIdSuffix}`}>
          <p className="text-sm font-semibold text-amber-900 mb-2">Quelle succursale {f.nom} ?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {magasins.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMagasinId(m.id)}
                data-testid={`btn-magasin-${m.id}${testIdSuffix}`}
                className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition ${magasinId === m.id ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
              >
                <div className="font-medium">{m.nom}</div>
                <div className={`text-[11px] ${magasinId === m.id ? 'text-amber-100' : 'text-slate-500'}`}>{m.adresse}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* 🆕 [2026-06-11 v4] Heures d'ouverture du fournisseur/magasin sélectionné
          NB : data-testid SANS suffixe pour faciliter l'accès par les tests E2E
          (le bloc est unique à l'écran : soit le picker chef, soit le picker CP). */}
      {selectionComplete && (
        <div
          className="border-t border-amber-200 pt-3 space-y-2"
          data-testid="heures-ouverture-block"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-amber-900">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-semibold">Heures d'ouverture</span>
            </div>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${badgeStyles[statut.badge_color] || badgeStyles.gray}`}
              data-testid="heures-badge-statut"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {statut.label}
            </span>
          </div>
          {statut.disponible ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-white border border-amber-200 px-3 py-2 text-xs text-slate-700"
              data-testid="heures-table"
            >
              {groupesHeures.map((g, i) => (
                <div key={i} className="flex justify-between">
                  <span className="font-medium">{g.label}</span>
                  <span className={g.heures === 'Fermé' ? 'text-rose-600' : 'text-slate-700'}>{g.heures}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs italic text-slate-500 bg-white border border-dashed border-amber-300 rounded px-2 py-1.5">
              Heures non renseignées — à compléter dans Gestion des Produits.
            </p>
          )}
        </div>
      )}
      {/* 🆕 [2026-06-11 v5] Sélecteur "Quand ramasser ?" — visible une fois le
          fournisseur (et magasin éventuel) choisi. Permet au chef de décider :
          aujourd'hui ou demain matin pour commencer la journée. */}
      {selectionComplete && setQuand && (
        <div
          className="border-t border-amber-200 pt-3 space-y-2"
          data-testid={`ramassage-quand-block${testIdSuffix}`}
        >
          <p className="text-sm font-semibold text-amber-900">Quand ramasser ?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setQuand('aujourd_hui')}
              data-testid="ramassage-quand-aujourd-hui"
              className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition ${quand === 'aujourd_hui' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
            >
              <div className="font-medium">🌞 Aujourd'hui</div>
              <div className={`text-[11px] ${quand === 'aujourd_hui' ? 'text-amber-100' : 'text-slate-500'}`}>Prête à être récupérée dans la journée</div>
            </button>
            <button
              type="button"
              onClick={() => setQuand('demain')}
              data-testid="ramassage-quand-demain-matin"
              className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition ${quand === 'demain' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
            >
              <div className="font-medium">🌅 Demain matin</div>
              <div className={`text-[11px] ${quand === 'demain' ? 'text-amber-100' : 'text-slate-500'}`}>Pour commencer la journée</div>
            </button>
          </div>
          {/* Warning non bloquant : Aujourd'hui mais magasin fermé maintenant */}
          {showWarningFerme && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-400 bg-amber-100 px-3 py-2 text-xs text-amber-900"
              data-testid="ramassage-quand-warning-ferme"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>{statut.label}.</strong> Ce magasin est actuellement fermé — la CP ne pourra peut-être pas
                passer la commande à temps pour aujourd'hui. Confirmez le timing avec elle, ou choisissez « Demain matin ».
              </span>
            </div>
          )}
        </div>
      )}
      {/* Aperçu confirmation */}
      {selectionComplete && (
        <p className="text-xs text-amber-800 bg-white border border-amber-300 rounded p-2" data-testid={`fournisseur-confirmation-preview${testIdSuffix}`}>
          ✅ La commande sera passée chez <strong>{f.nom}{magasinId ? ` — ${magasins.find(mg => mg.id === magasinId)?.nom || ''}` : ''}</strong> et <strong>{quand === 'aujourd_hui' ? 'prête à être récupérée aujourd\'hui' : 'prête à être récupérée demain matin pour commencer la journée'}</strong>.
        </p>
      )}
    </div>
  );
}

export function BonTravailDetail({ bon, dossier, employees, onBack, onDelete, onToggleEtape, onEdit, onRefresh, onProlongerEquipement, prolongerState, autoOpenCommande, hideAddress = false }) {
  const { user, isProprietaire, isChefEquipe } = useAuth();
  const { putBon } = useBtDetailActions(bon);  // 🧱 Refactor BT Phase 3 — remplace les 4 axios.put
  const { canDoBonTravailAction } = usePermissions() || {};
  const canManage = isProprietaire || isChefEquipe;
  
  // Permissions spécifiques pour les boutons du BT (toutes dans bon_travail_actions)
  const canImprimer = canDoBonTravailAction ? canDoBonTravailAction('bt_imprimer') : false;
  const canSupprimerDetail = canDoBonTravailAction ? canDoBonTravailAction('bt_supprimer_detail') : false;
  const canVerrouillerCommande = canDoBonTravailAction ? canDoBonTravailAction('bt_verrouiller_commande') : false;  const canAjouterPhotos = canDoBonTravailAction ? canDoBonTravailAction('bt_ajouter_photos') : false;
  const canInspection = canDoBonTravailAction ? canDoBonTravailAction('bt_inspection') : false;
  const canInspectionVoir = canDoBonTravailAction ? canDoBonTravailAction('bt_inspection_voir') : false;
  const canSignalerIncident = canDoBonTravailAction ? canDoBonTravailAction('bt_signaler_incident') : false;
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Nouvelles clés bon_travail_actions branchées.
  const canEnvoyerPresentation      = canDoBonTravailAction ? canDoBonTravailAction('bon_envoyer_presentation') : false;
  const canLierDossier              = canDoBonTravailAction ? canDoBonTravailAction('bon_lier_dossier') : false;
  const canVoirDossiersLiesSection  = canDoBonTravailAction ? canDoBonTravailAction('bon_voir_dossiers_lies_section') : false;
  const canVoirDossiersLiesMontants = canDoBonTravailAction ? canDoBonTravailAction('bon_voir_dossiers_lies_montants') : false;
  const canApprouverCommande        = canDoBonTravailAction ? canDoBonTravailAction('bon_approuver_commande') : false;
  const canCommanderPreChantierCP   = canDoBonTravailAction ? canDoBonTravailAction('bon_commander_produits_pre_chantier_cp') : false;
  
  const [zonePreview, setZonePreview] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [teamInfo, setTeamInfo] = useState({});
  // 🆕 [Phase 41 BIS] Stats agrégées BT liés
  const [statsAgregees, setStatsAgregees] = useState(null);
  useEffect(() => {
    if (!bon?.id) return;
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    axios.get(`${API_URL}/crm/bons-travail/${bon.id}/stats-agregees`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStatsAgregees(res.data))
      .catch(() => setStatsAgregees(null));
  }, [bon?.id]);
  const isExt = bon.type_travaux === 'exterieur';
  const TypeIcon = isExt ? TreePine : Home;
  const zones = bon.zones || [];
  const etapesCompleted = (bon.etapes || []).filter(e => e.completee).length;
  const [showCommander, setShowCommander] = useState(!!autoOpenCommande);
  const [commandeForm, setCommandeForm] = useState({});
  const [savingCommande, setSavingCommande] = useState(false);
  // 🆕 2026-04-30 — Choix livraison/quand (CP + chef d'équipe)
  // - CP (1ère commande) : seulement `destination` (entrepot|client)
  // - Chef d'équipe : `quand` (aujourd_hui|demain). Aujourd'hui ⇒ client forcé. Demain ⇒ choisir destination.
  // 🆕 [2026-06-11 v5] Valeur par défaut = 'demain' (= "Demain matin pour commencer la journée")
  //                    pour éviter au chef de devoir cliquer le bouton 95% du temps.
  const [livraisonQuand, setLivraisonQuand] = useState('demain'); // 'aujourd_hui' | 'demain'
  // 🆕 [2026-06-11 v5] State séparé pour le sous-sélecteur du picker Ramassage
  // fournisseur. Évite de re-render le bloc destination quand le chef choisit
  // « Aujourd'hui » (ramassage) — semantique différente du « Aujourd'hui » du
  // bloc parent qui force la livraison chez le client.
  const [ramassageQuand, setRamassageQuand] = useState('demain'); // 'aujourd_hui' | 'demain'
  const [livraisonDestination, setLivraisonDestination] = useState(''); // '' | 'entrepot' | 'client' | 'fournisseur'
  // 🆕 [2026-06-11 v2] Ramassage chez un fournisseur (Sherwin-Williams, Benjamin Moore, etc.)
  // Source : GET /api/fournisseurs/ramassage (générique, remplace l'ancien sherwin-williams-magasins hardcodé)
  const [fournisseursRamassage, setFournisseursRamassage] = useState([]);  // [{id, nom, adresse, telephone, magasins:[{id,nom,adresse,telephone}]}]
  const [fournisseurId, setFournisseurId] = useState('');                  // id du fournisseur sélectionné
  const [magasinId, setMagasinId] = useState('');                          // id du magasin sélectionné (si le fournisseur en a)
  // Hors catalogue
  const [extraProduits, setExtraProduits] = useState([]);   // [{produit_id, nom, unite, quantite, categorie}]
  const [catalogProduits, setCatalogProduits] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [sendingPresentation, setSendingPresentation] = useState(false);
  const [showPresentationModal, setShowPresentationModal] = useState(false);
  const { previewHtml, loadingPreview, fetchPreview } = useEmailPreview();
  const [presentationTemplates, setPresentationTemplates] = useState([]);
  const [presentationData, setPresentationData] = useState({ sujet: '', contenu: '', email_client: '', inclure_lien: true });
  const [infosEntreprise, setInfosEntreprise] = useState(null);
  const [showServiceCallDialog, setShowServiceCallDialog] = useState(false);
  const [prolongerDates, setProlongerDates] = useState({}); // {eq_id: nbJours (number)}
  const [showQuickAddEmploye, setShowQuickAddEmploye] = useState(false);
  const [showLierDossier, setShowLierDossier] = useState(false);
  const [searchLierDossier, setSearchLierDossier] = useState('');
  const [dossierSearchResults, setDossierSearchResults] = useState([]);

  // Recherche de dossiers pour liaison jumelé — seulement statut accepté
  useEffect(() => {
    if (searchLierDossier.length < 2) { setDossierSearchResults([]); return; }
    const timer = setTimeout(() => {
      axios.get(`${API_URL}/api/crm/dossiers?search=${encodeURIComponent(searchLierDossier)}&limit=10&statut=accepte`).then(r => r.data)
        .then(d => setDossierSearchResults((d.dossiers || []).filter(dos => dos.signature_obtenue && ['accepte', 'en_attente_production', 'en_production'].includes(dos.statut))))
        .catch(() => setDossierSearchResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchLierDossier]);
  const [quickAddEmpId, setQuickAddEmpId] = useState('');
  const [savingEmp, setSavingEmp] = useState(false);
  const [btPhotos, setBtPhotos] = useState(bon.photos || []);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // ✅ FIX: Charger les employés si manquants (pour afficher les téléphones)
  const [localEmployees, setLocalEmployees] = useState(employees || []);
  
  useEffect(() => {
    // Si employees est vide ou ne contient pas de téléphones, les recharger
    if (!employees || employees.length === 0 || !employees.some(e => e.telephone)) {
      axios.get(`${API_URL}/api/employees`).then(res => {
        if (res.status === 200 && res.data.employees) {
          setLocalEmployees(res.data.employees);
        }
      }).catch(() => {});
    } else {
      setLocalEmployees(employees);
    }
  }, [employees]);

  // ✅ Compresser image avant upload pour éviter timeout
  const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height *= maxWidth / width;
              width = maxWidth;
            } else {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadBtPhotos = async (files) => {
    if (!files.length) return;
    setUploadingPhotos(true);
    setUploadProgress(0);
    
    try {
      // ✅ Compresser images d'abord
      toast.info(`Préparation de ${files.length} photo(s)...`);
      const compressedFiles = await Promise.all(
        files.map(f => compressImage(f))
      );
      
      const fd = new FormData();
      compressedFiles.forEach(f => fd.append('files', f));
      
      setUploadProgress(50);
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(50 + (pct / 2));
        }
      });
      
      const data = res.data;
      if (res.status === 200) {
        setBtPhotos(prev => [...prev, ...data.photos]);
        toast.success(`${data.photos.length} photo(s) ajoutée(s)`);
      } else { toast.error(data.detail || 'Erreur upload'); }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Erreur upload. Fichiers trop volumineux ?');
    }
    finally { 
      setUploadingPhotos(false); 
      setUploadProgress(0);
    }
  };

  const handleDeleteBtPhoto = async (idx) => {
    try {
      const res = await axios.delete(`${API_URL}/api/crm/bons-travail/${bon.id}/photos/${idx}`);
      if (res.status === 200) {
        setBtPhotos(prev => prev.filter((_, i) => i !== idx));
        toast.success('Photo supprimée');
      } else { toast.error('Erreur suppression'); }
    } catch { toast.error('Erreur réseau'); }
  };

  // Charger le catalogue quand la section commander s'ouvre
  useEffect(() => {
    if (showCommander && catalogProduits.length === 0) {
      setLoadingCatalog(true);
      // ✅ Charger UNIQUEMENT les produits catalogue (pas l'entrepôt)
      // L'entrepôt est utilisé uniquement dans la section dédiée "Entrepôt"
      axios.get(`${API_URL}/api/produits?actif=true`)
        .then(r => r.status === 200 ? r.data : { produits: [] })
        .then((catalogData) => {
          const catalogItems = (catalogData.produits || []).map(p => ({
            id: p.id,
            nom: p.nom,
            categorie: p.categorie || 'Catalogue',
            unite: p.unite || 'un',
            source: 'catalogue',
          }));
          setCatalogProduits(catalogItems);
        })
        .catch(() => {})
        .finally(() => setLoadingCatalog(false));
    }
    if (!showCommander) {
      setExtraProduits([]);
      setCatalogSearch('');
    }
  }, [showCommander]);

  // 🆕 [2026-06-11 v2] Charger la liste générique des fournisseurs (avec magasins)
  // dès l'ouverture du panneau commander (cached après 1er appel).
  useEffect(() => {
    if (showCommander && fournisseursRamassage.length === 0) {
      axios.get(`${API_URL}/api/fournisseurs/ramassage`)
        .then(r => r.status === 200 ? r.data : { fournisseurs: [] })
        .then(d => {
          // 🆕 [2026-06-11 v3] Le backend retourne TOUS les fournisseurs actifs.
          // Côté UI on n'affiche dans le sélecteur que ceux qui ont une adresse
          // OU au moins un magasin (`disponible_pour_ramassage !== false`).
          // La page Gestion des Produits, elle, affiche tout (pour édition).
          const tous = d.fournisseurs || [];
          const dispo = tous.filter(fr => fr.disponible_pour_ramassage !== false);
          setFournisseursRamassage(dispo);
        })
        .catch(() => {});
    }
  }, [showCommander, fournisseursRamassage.length]);

  const addExtraProduit = (produit) => {
    setExtraProduits(prev => {
      const exists = prev.find(p => p.produit_id === produit.id);
      if (exists) return prev.map(p => p.produit_id === produit.id ? { ...p, quantite: p.quantite + 1 } : p);
      return [...prev, { produit_id: produit.id, nom: produit.nom, unite: produit.unite || 'un', quantite: 1, categorie: produit.categorie || '' }];
    });
    setCatalogSearch('');
  };

  const handleQuickAddEmploye = async () => {
    if (!quickAddEmpId) return;
    const emp = employees.find(e => e.id === quickAddEmpId);
    if (!emp) return;
    const already = (bon.employes_assignes || []).find(e => e.employee_id === quickAddEmpId);
    if (already) { setShowQuickAddEmploye(false); return; }
    setSavingEmp(true);
    const updated = [...(bon.employes_assignes || []), { employee_id: emp.id, employee_nom: `${emp.prenom} ${emp.nom}`, est_chef: false }];
    try {
      // 🛡️ [v13.62 — 2026-02-28] Retirer les champs masqués Phase 81 (heures_budgetees=null
      //   pour non-owners) qui font échouer la validation Pydantic 422. Cf. RCA_V13_62_PRESENCE_PAR_JOUR.md
      // 🧱 Refactor BT Phase 3 — putBon unique (corps identique : strip({ ...bon, employes_assignes }))
      const res = await putBon({ employes_assignes: updated });
      if (res.status === 200) { setShowQuickAddEmploye(false); setQuickAddEmpId(''); toast.success('Employé ajouté'); if (onRefresh) onRefresh(); }
    } catch (e) { toast.error("Erreur lors de l'ajout"); }
    setSavingEmp(false);
  };

  const handleQuickRemoveEmploye = async (empId) => {
    const updated = (bon.employes_assignes || []).filter(e => e.employee_id !== empId);

    // ✅ FIX: Ne PAS recalculer heures_budgetees quand on retire un employé
    // Les heures budgetées restent fixes (définies par la soumission)
    // Seul le calcul d'estimation (jours nécessaires) change dynamiquement

    try {
      // 🛡️ [v13.62 — 2026-02-28] Retirer les champs masqués Phase 81.
      // 🧱 Refactor BT Phase 3 — putBon unique
      await putBon({ employes_assignes: updated });
      toast.success('Employé retiré');
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Erreur lors du retrait'); }
  };

  // Vérifier si toutes les nacelles sont marquées comme terminées
  const allEquipementsTermines = (bon.equipements || []).length === 0 || 
    (bon.equipements || []).every(eq => eq.utilisation_terminee);

  // Charger les infos du client + employés (représentant, chargée de projet, chef d'équipe)
  useEffect(() => {
    if (dossier?.client_id) {
      axios.get(`${API_URL}/api/crm/clients/${dossier.client_id}`)
        .then(res => res.data)
        .then(data => setClientInfo(data))
        .catch(() => {});
    }
    // Charger les téléphones des employés liés au dossier
    axios.get(`${API_URL}/api/employees`)
      .then(res => res.data)
      .then(data => {
        const emps = data.employees || [];
        const info = {};
        const ids = [dossier?.representant_id, dossier?.charge_projet_id, dossier?.chef_equipe_id, bon?.chef_equipe_id].filter(Boolean);
        ids.forEach(id => {
          const emp = emps.find(e => e.id === id);
          if (emp) info[id] = emp;
        });
        setTeamInfo(info);
      })
      .catch(() => {});
  }, [dossier?.client_id, dossier?.representant_id, dossier?.charge_projet_id, dossier?.chef_equipe_id, bon?.chef_equipe_id]);

  // Initialiser le formulaire de commande
  useEffect(() => {
    if (bon.produits?.length > 0) {
      const initial = {};
      bon.produits.forEach((p, i) => { initial[i] = { quantite: 1, selected: false }; });
      setCommandeForm(initial);
    }
  }, [bon.produits]);

  // Marquer utilisation équipement comme terminée
  const handleEquipementTermine = async (eqIdx) => {
    try {
      const updatedEquipements = [...(bon.equipements || [])];
      updatedEquipements[eqIdx] = { 
        ...updatedEquipements[eqIdx], 
        utilisation_terminee: true,
        date_fin_utilisation: new Date().toISOString()
      };
      const authToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      // 🛡️ [v13.62 — 2026-02-28] Retirer les champs masqués Phase 81.
      // 🧱 Refactor BT Phase 3 — putBon unique (en-tête Authorization explicite conservé tel quel)
      await putBon({ equipements: updatedEquipements },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success('Utilisation équipement terminée — équipement libéré dans le calendrier Location!');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Marquer lavage à pression comme complété
  const handleLavagePressionComplete = async () => {
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/lavage-pression-complete`);
      toast.success('Lavage à pression marqué comme complété!');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // 🆕 2026-05-12 — Toggle manuel du lavage (cas BT supprimé/recréé ou annulation).
  const handleToggleLavage = async (field, value, successMsg) => {
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/toggle-lavage`, { field, value });
      toast.success(successMsg);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Erreur lors du toggle lavage');
    }
  };

  // Ouvrir le dialog de présentation avec template
  const handleOpenPresentation = async () => {
    try {
      const [tplRes, entRes] = await Promise.all([
        axios.get(`${API_URL}/api/messages-templates?categorie=presentation`),
        axios.get(`${API_URL}/api/entreprise/infos`)
      ]);
      const tplData = tplRes.status === 200 ? tplRes.data : { templates: [] };
      const entData = entRes.status === 200 ? entRes.data : {};
      const templates = tplData.templates || [];
      setPresentationTemplates(templates);
      setInfosEntreprise(entData);

      const initialData = { sujet: '', contenu: '', email_client: clientInfo?.email || '', inclure_lien: true };

      if (templates.length > 0) {
        const t = templates[0];
        const vars = getPresentationVars(entData);
        let sujet = t.sujet || '';
        let contenu = t.contenu || '';
        Object.entries(vars).forEach(([k, v]) => {
          sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
          contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        });
        initialData.sujet = sujet;
        initialData.contenu = contenu;
      }
      setPresentationData(initialData);
    } catch (e) { console.error(e); }
    setShowPresentationModal(true);
  };

  const getPresentationVars = (ent) => {
    // Tableau HTML des produits (même format que le courriel original)
    const produitsTbody = (bon.produits || []).map(p =>
      `<tr><td style="padding:8px;border:1px solid #ddd;">${p.produit_nom}</td><td style="padding:8px;border:1px solid #ddd;">${p.fini || '—'}</td><td style="padding:8px;border:1px solid #ddd;">${p.emplacement || '—'}</td><td style="padding:8px;border:1px solid #ddd;">${p.code_couleur || 'À définir'}</td></tr>`
    ).join('');
    const tableauProduits = `<table style="width:100%;border-collapse:collapse;margin:16px 0;"><thead><tr style="background:#f3f4f6;"><th style="padding:8px;border:1px solid #ddd;text-align:left;">Produit</th><th style="padding:8px;border:1px solid #ddd;text-align:left;">Fini</th><th style="padding:8px;border:1px solid #ddd;text-align:left;">Emplacement</th><th style="padding:8px;border:1px solid #ddd;text-align:left;">Code couleur</th></tr></thead><tbody>${produitsTbody}</tbody></table>`;

    // Dates de chantier — pour projets pluie avec plage: "entre le X et le Y"
    let datesChantier;
    if (bon.jours_pluie && bon.pluie_date_debut && bon.pluie_date_fin) {
      const debut = new Date(bon.pluie_date_debut + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      const fin = new Date(bon.pluie_date_fin + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      datesChantier = `entre le ${debut} et le ${fin}`;
    } else {
      datesChantier = bon.dates_chantier?.length > 0
        ? bon.dates_chantier.map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })).join(', ')
        : 'Les dates seront confirmées en temps et lieu.';
    }

    // Téléphones via teamInfo
    const repId = dossier?.representant_id;
    const cpId = dossier?.charge_projet_id;
    const ceId = bon?.chef_equipe_id || dossier?.chef_equipe_id;

    // Logo email — URL HTTPS publique (Gmail bloque les images base64)
    const logoEmail = ent?.logo_email
      ? `<div style="text-align:left;margin-bottom:16px;"><img src="${API_URL}${ent.logo_email}" alt="${ent?.nom_entreprise || ''}" style="width:180px;height:auto;" /></div>`
      : '';

    // Avis Google (lien cliquable)
    const avisGoogle = ent?.lien_avis_google
      ? `<a href="${ent.lien_avis_google}" style="color:#2563eb;text-decoration:underline;font-weight:bold;">Avis Google</a>`
      : '';

    return {
      client_nom: clientInfo?.nom || dossier?.client_nom || '',
      // nom_dossier retourne le NUMÉRO (jamais le nom interne au client)
      nom_dossier: dossier?.numero_dossier || dossier?.nom_dossier || '',
      numero_dossier: dossier?.numero_dossier || '',
      adresse_travaux: [dossier?.adresse_travaux, dossier?.ville_travaux, dossier?.code_postal_travaux].filter(Boolean).join(', '),
      dates_chantier: datesChantier,
      chef_equipe: bon?.chef_equipe_nom || dossier?.chef_equipe_nom || '',
      telephone_chef_equipe: (ceId && teamInfo[ceId]?.telephone) || '',
      charge_projet: dossier?.charge_projet_nom || '',
      telephone_charge_projet: (cpId && teamInfo[cpId]?.telephone) || '',
      representant: dossier?.representant_nom || '',
      telephone_representant: (repId && teamInfo[repId]?.telephone) || '',
      tableau_produits: tableauProduits,
      lien_codes_couleur: '{LIEN_CODES_COULEUR}',
      nom_entreprise: ent?.nom_entreprise || 'Peinture Résidentielle QC',
      telephone_entreprise: ent?.telephone || '',
      adresse_entreprise: ent?.adresse || '',
      numero_rbq: ent?.numero_rbq ? `RBQ : ${ent.numero_rbq}` : '',
      avis_google: avisGoogle,
      logo_email: logoEmail,
    };
  };

  const handleSelectPresentationTemplate = (template) => {
    const vars = getPresentationVars(infosEntreprise);
    let sujet = template.sujet || '';
    let contenu = template.contenu || '';
    Object.entries(vars).forEach(([k, v]) => {
      sujet = sujet.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
      contenu = contenu.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
    });
    setPresentationData(prev => ({ ...prev, sujet, contenu }));
  };

  // Envoyer courriel de présentation
  const handleSendPresentation = async () => {
    if (!presentationData.email_client) { toast.error('Email du client requis'); return; }
    if (!presentationData.sujet || !presentationData.contenu) { toast.error('Sujet et contenu requis'); return; }
    setSendingPresentation(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/envoyer-presentation`, {
          email_client: presentationData.email_client,
          sujet: presentationData.sujet,
          contenu: presentationData.contenu,
          inclure_lien: presentationData.inclure_lien
        });
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || 'Erreur');
      toast.success(data.message || 'Courriel de présentation envoyé!');
      setShowPresentationModal(false);
    } catch (e) { toast.error(e.message || 'Erreur lors de l\'envoi'); }
    finally { setSendingPresentation(false); }
  };

  // Passer une commande
  const handlePasserCommande = async () => {
    const produitsReguliers = Object.entries(commandeForm)
      .filter(([_, v]) => v.selected && v.quantite > 0)
      .map(([idx, v]) => ({
        ...bon.produits[parseInt(idx)],
        quantite_commandee: v.quantite
      }));

    const produitsExtras = extraProduits
      .filter(p => p.quantite > 0)
      .map(p => ({
        produit_id: p.produit_id,
        produit_nom: p.nom,
        nom: p.nom,
        unite: p.unite,
        categorie: p.categorie,
        quantite_commandee: p.quantite,
        hors_catalogue: true,
      }));

    const produitsACommander = [...produitsReguliers, ...produitsExtras];

    if (produitsACommander.length === 0) {
      toast.error('Sélectionnez au moins un produit');
      return;
    }

    // 🆕 2026-02-20 — Règle métier mise à jour :
    //   La chargée de projet (CP) / adjoint admin peuvent passer AUTANT de
    //   commandes que nécessaire TANT QU'aucun employé n'a punché sur ce BT
    //   (utile pour les jobs où on commande des produits à 2-3 semaines d'écart
    //   avant le début du chantier). Dès qu'un employé punche, le chef d'équipe
    //   prend le relais (lien bonus + critère 14h).
    const travailCommence = !!bon.travail_commence;
    if (travailCommence && !isChefEquipe && !isProprietaire) {
      toast.error("Le chantier est commencé : seul le chef d'équipe peut passer les commandes supplémentaires (lié au bonus & critère 14h).");
      return;
    }

    // 🆕 2026-04-30 — Validation livraison + résolution du destination final
    // 🆕 [2026-06-11 v2] Nouveau mode 'fournisseur' (ramassage générique chez n'importe quel fournisseur)
    let destinationFinale = '';
    let quandFinale = '';
    let fournisseurFinal = null;  // {id, nom, adresse} ou null
    let magasinFinal = null;      // {id, nom, adresse} ou null si fournisseur sans magasins
    if (isChefEquipe && !isProprietaire) {
      // Chef d'équipe : doit avoir choisi quand + (si demain) destination
      if (!livraisonQuand) {
        toast.error('Choisissez quand vous voulez recevoir la commande');
        return;
      }
      quandFinale = livraisonQuand;
      if (livraisonQuand === 'aujourd_hui') {
        destinationFinale = 'client'; // règle métier : aujourd'hui ⇒ chez le client
      } else {
        if (!livraisonDestination) {
          toast.error('Choisissez où livrer la commande');
          return;
        }
        destinationFinale = livraisonDestination;
        // 🆕 [2026-06-11 v5] Si Ramassage fournisseur, on prend le timing du
        // sous-sélecteur dédié (peut être "aujourd_hui" indépendamment du
        // "Demain" choisi à l'étape précédente — c'est la sémantique v5).
        if (destinationFinale === 'fournisseur') {
          quandFinale = ramassageQuand || 'demain';
        }
      }
    } else {
      // CP / proprio (1ère commande) : seulement choisir la destination
      if (!livraisonDestination) {
        toast.error('Choisissez où livrer la commande (entrepôt, client ou ramassage fournisseur)');
        return;
      }
      destinationFinale = livraisonDestination;
    }
    // 🆕 [2026-06-11 v2] Si ramassage fournisseur, le fournisseur est obligatoire
    if (destinationFinale === 'fournisseur') {
      if (!fournisseurId) {
        toast.error('Choisissez le fournisseur où la commande sera ramassée');
        return;
      }
      const f = fournisseursRamassage.find(fr => fr.id === fournisseurId);
      if (!f) {
        toast.error('Fournisseur invalide');
        return;
      }
      fournisseurFinal = { id: f.id, nom: f.nom, adresse: f.adresse || '' };
      // Si le fournisseur a des magasins, on impose le choix d'un magasin
      if ((f.magasins || []).length > 0) {
        if (!magasinId) {
          toast.error(`Choisissez la succursale ${f.nom}`);
          return;
        }
        const m = (f.magasins || []).find(mg => mg.id === magasinId);
        if (!m) {
          toast.error('Succursale invalide');
          return;
        }
        magasinFinal = { id: m.id, nom: m.nom, adresse: m.adresse || '' };
      }
    }

    // ✅ Double confirmation pour le chef d'équipe avant d'envoyer la commande
    if (isChefEquipe && !isProprietaire) {
      const recapProduits = produitsACommander
        .map(p => `• ${p.produit_nom || p.nom || 'Produit'} : ${p.quantite_commandee} ${p.unite || ''}`.trim())
        .join('\n');
      let livLabel;
      if (destinationFinale === 'entrepot') livLabel = 'Entrepôt';
      else if (destinationFinale === 'client') livLabel = 'Chez le client';
      else if (destinationFinale === 'fournisseur' && fournisseurFinal) {
        const lieu = magasinFinal ? `${fournisseurFinal.nom} — ${magasinFinal.nom}` : fournisseurFinal.nom;
        const adr = magasinFinal?.adresse || fournisseurFinal.adresse || '';
        // 🆕 [2026-06-11 v5] Phrase de confirmation adaptée selon le moment choisi
        const quandPhrase = quandFinale === 'aujourd_hui'
          ? 'prête à être récupérée aujourd\'hui'
          : 'prête à être récupérée demain matin';
        livLabel = `Ramassage chez ${lieu}\n   ${adr}\n   (${quandPhrase})`;
      } else livLabel = '—';
      const quandLabel = quandFinale === 'aujourd_hui' ? "🌞 Aujourd'hui" : '🌅 Demain matin';
      const confirmMsg = `Confirmer la commande ?\n\n${recapProduits}\n\n📦 Livraison : ${livLabel}\n📅 Quand : ${quandLabel}\n\nLa chargée de projet sera automatiquement notifiée (courriel + SMS).`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    setSavingCommande(true);
    try {
      const now = new Date();
      const heure = now.getHours();
      const minutes = now.getMinutes();
      const apres14h = heure > 14 || (heure === 14 && minutes > 0);
      const heureFormatee = `${String(heure).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`;

      // 🆕 2026-02-20 — Logique de commande mise à jour :
      //   Tant que le chantier n'est pas commencé (aucun punch), la CP peut
      //   passer plusieurs commandes successives, toutes auto-approuvées.
      //   Dès qu'un punch existe, c'est le chef d'équipe qui commande et ses
      //   commandes passent en attente d'approbation de la CP. Les critères
      //   14h / bonus s'appliquent uniquement aux commandes du chef.
      const commandeParChefVsCommencement = isChefEquipe && travailCommence && !isProprietaire;
      // 🆕 [2026-06-11 v2] Snapshot générique fournisseur — préserve nom+adresse
      // même si la config des fournisseurs change plus tard. Backward compat :
      // si Sherwin-Williams, on dépose aussi les champs legacy `sherwin_magasin_*`
      // pour que les badges historique existants continuent à s'afficher.
      const isSherwinLegacy = (fournisseurFinal?.nom || '').toLowerCase().includes('sherwin');
      const nouvelleCommande = {
        date: now.toISOString(),
        produits: produitsACommander,
        commandee_par: user?.prenom ? `${user.prenom} ${user.nom}` : 'Inconnu',
        // Flag « après 14h » seulement pour les commandes du chef (impactant le critère bonus)
        apres_15h30: apres14h && commandeParChefVsCommencement,
        apres_14h: apres14h && commandeParChefVsCommencement,
        heure_commande: heureFormatee,
        est_premiere_commande: !(bon.historique_commandes?.length > 0),
        livraison_destination: destinationFinale,
        livraison_quand: quandFinale || null,
        // 🆕 [2026-06-11 v2] Snapshot fournisseur générique
        fournisseur_id: fournisseurFinal?.id || null,
        fournisseur_nom: fournisseurFinal?.nom || null,
        fournisseur_adresse: fournisseurFinal?.adresse || null,
        magasin_id: magasinFinal?.id || null,
        magasin_nom: magasinFinal?.nom || null,
        magasin_adresse: magasinFinal?.adresse || null,
        // Backward compat legacy Sherwin (badge historique + ancien SMS template)
        sherwin_magasin_id: isSherwinLegacy ? (magasinFinal?.id || null) : null,
        sherwin_magasin_nom: isSherwinLegacy ? (magasinFinal?.nom || null) : null,
        sherwin_magasin_adresse: isSherwinLegacy ? (magasinFinal?.adresse || null) : null,
        // Commande du chef après début chantier → attente approbation CP
        // Commande CP / admin / propriétaire → auto-approuvée
        statut_approbation: commandeParChefVsCommencement ? 'en_attente_cp' : 'approuvee_auto',
      };

      const historiqueCommandes = [...(bon.historique_commandes || []), nouvelleCommande];

      // Si c'est la première commande, marquer les produits commandés comme verrouillés
      const produitsUpdated = (bon.produits || []).map((p, idx) => {
        const commandeProduit = produitsReguliers.find(pr => 
          pr.produit_nom === p.produit_nom || pr.produit_id === p.produit_id
        );
        if (commandeProduit && !bon.historique_commandes?.length) {
          return { ...p, commande_initiale_faite: true, quantite: commandeProduit.quantite_commandee || p.quantite };
        }
        return p;
      });

      const authToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      // 🛡️ [v13.62 — 2026-02-28] Retirer les champs masqués Phase 81.
      // 🧱 Refactor BT Phase 3 — putBon unique (en-tête Authorization explicite conservé tel quel)
      await putBon({ historique_commandes: historiqueCommandes, produits: produitsUpdated },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // ✅ Notifier la CP si la commande vient du chef d'équipe (email + SMS + audit trail)
      if (isChefEquipe && !isProprietaire) {
        try {
          await axios.post(
            `${API_URL}/api/crm/bons-travail/${bon.id}/notifier-cp-apres-commande`,
            {
              produits: produitsACommander,
              commandee_par: user?.prenom ? `${user.prenom} ${user.nom || ''}`.trim() : 'Chef d\'équipe',
              heure_commande: heureFormatee,
              commande_date: nouvelleCommande.date,  // pour enrichir l'audit trail
              // 🆕 2026-04-30 — métadonnées livraison incluses dans le mail/SMS CP
              livraison_destination: destinationFinale,
              livraison_quand: quandFinale,
              // 🆕 [2026-06-11 v2] Fournisseur générique (nom + adresse + magasin éventuel)
              fournisseur_id: fournisseurFinal?.id || '',
              fournisseur_nom: fournisseurFinal?.nom || '',
              fournisseur_adresse: fournisseurFinal?.adresse || '',
              magasin_id: magasinFinal?.id || '',
              magasin_nom: magasinFinal?.nom || '',
              magasin_adresse: magasinFinal?.adresse || '',
              // Backward compat legacy Sherwin pour les templates SMS/email existants
              sherwin_magasin_id: isSherwinLegacy ? (magasinFinal?.id || '') : '',
              sherwin_magasin_nom: isSherwinLegacy ? (magasinFinal?.nom || '') : '',
              sherwin_magasin_adresse: isSherwinLegacy ? (magasinFinal?.adresse || '') : '',
            },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        } catch (e) {
          console.warn('Notification CP échouée (non-bloquant):', e);
        }
      }

      // ✅ FIX (2026-02-20): Le critère "avant 14h" s'applique UNIQUEMENT
      //   aux commandes du chef d'équipe APRÈS début du chantier (lien bonus).
      //   Les commandes pré-chantier de la CP n'impactent jamais ce critère.
      const commandeParChef = user?.employee_id && bon.chef_equipe_id && user.employee_id === bon.chef_equipe_id;
      const critereApplicable = apres14h && commandeParChef && travailCommence;

      if (critereApplicable) {
        try {
          await axios.post(`${API_URL}/api/performance/gestion-contrat/${bon.id}/commande-tardive`, {
            heure_commande: heureFormatee,
            produits: produitsACommander.map(p => p.produit_nom || p.nom || 'Produit').join(', '),
            commandee_par: user?.prenom ? `${user.prenom} ${user.nom}` : 'Inconnu',
            commandee_par_id: user?.employee_id || null,
          });
          toast.warning(`⚠️ Commande passée après 14h (${heureFormatee}) — Critère "Gallons avant 14h" automatiquement marqué comme échoué`);
        } catch (e) {
          console.error('Erreur auto-remplissage GC:', e);
          toast.warning(`Commande après 14h (${heureFormatee}) - Pensez à mettre à jour le formulaire Gestion du Contrat`);
        }
      } else if (apres14h && !travailCommence) {
        // Commande pré-chantier par CP/admin → pas d'impact sur critère
        toast.success('Commande passée !');
      } else if (apres14h && !commandeParChef) {
        toast.info(`Commande passée à ${heureFormatee} (après 14h) — Non appliquée au critère du chef car ce n'est pas lui qui a commandé.`);
      } else {
        toast.success('Commande passée !');
      }
      // 🆕 [2026-06-11 v2] Confirmation dédiée Ramassage fournisseur (générique)
      if (destinationFinale === 'fournisseur' && fournisseurFinal) {
        const lieu = magasinFinal ? `${fournisseurFinal.nom} — ${magasinFinal.nom}` : fournisseurFinal.nom;
        const adr = magasinFinal?.adresse || fournisseurFinal.adresse || '';
        // 🆕 [2026-06-11 v5] Wording adapté selon "Aujourd'hui" / "Demain matin"
        const quandPhrase = quandFinale === 'aujourd_hui'
          ? 'et prête à être récupérée 🌞 aujourd\'hui'
          : 'et prête à être récupérée 🌅 demain matin pour commencer la journée';
        toast.success(
          `✅ Commande envoyée à la chargée de projet. Elle sera passée au ${lieu} (${adr}) ${quandPhrase}.`,
          { duration: 9000 }
        );
      }
      setShowCommander(false);
      // 🆕 reset choix livraison après envoi
      // 🆕 [2026-06-11 v5] Reset à 'demain' (valeur par défaut), pas chaîne vide
      setLivraisonQuand('demain');
      setRamassageQuand('demain');
      setLivraisonDestination('');
      setFournisseurId('');
      setMagasinId('');
      if (onRefresh) onRefresh();
    } catch (e) {
      // 🐛 2026-02-20 — Afficher le vrai message d'erreur backend au lieu du générique
      // pour faciliter le diagnostic des bugs de commande chef d'équipe.
      const detail = e?.response?.data?.detail;
      let msg = 'Erreur lors de la commande';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') {
        msg = detail.message || JSON.stringify(detail);
      } else if (e?.message) {
        msg = e.message;
      }
      console.error('Erreur commande chef d\'équipe:', e?.response?.status, e?.response?.data || e);
      toast.error(msg);
    } finally {
      setSavingCommande(false);
    }
  };

  // Render step description with clickable zone references
  const renderDescription = (text) => {
    if (!isExt || zones.length === 0) return text;
    return text.replace(/(zone\s*(\d+))/gi, (match, full, num) => {
      return `<ZONE_LINK_${num}>`;
    });
  };

  const DescriptionWithZones = ({ text }) => {
    if (!isExt || zones.length === 0) return <span>{text}</span>;
    const parts = text.split(/(zone\s*\d+)/gi);
    return (
      <span>
        {parts.map((part, i) => {
          const zoneMatch = part.match(/zone\s*(\d+)/i);
          if (zoneMatch) {
            const zNum = parseInt(zoneMatch[1]);
            const zone = zones.find(z => z.numero === zNum);
            return (
              <button key={`zone-${i}`} onClick={() => zone && setZonePreview(zone)}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold transition-colors ${zone ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer' : 'bg-gray-100 text-gray-500'}`}>
                <MapPin className="w-3 h-3" />{part}
              </button>
            );
          }
          return <span key={`part-${i}`}>{part}</span>;
        })}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* ⚠️ Badge conflits équipements (Phase 2 Location) */}
      {Array.isArray(bon.conflits_equipements_detectes) && bon.conflits_equipements_detectes.length > 0 && (
        <div
          className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-3 no-print"
          data-testid="bt-conflits-equipements-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {bon.conflits_equipements_detectes.length} conflit(s) d'équipement(s) après déplacement des dates
              </p>
              <ul className="text-xs text-amber-800 mt-1 space-y-0.5 max-h-24 overflow-auto">
                {bon.conflits_equipements_detectes.slice(0, 10).map((c, idx) => (
                  <li key={idx} className="truncate">
                    • <span className="font-medium">{c.equipement_nom}</span> le {c.date} — conflit avec <span className="italic">{c.conflit_avec}</span>
                  </li>
                ))}
                {bon.conflits_equipements_detectes.length > 10 && (
                  <li>… et {bon.conflits_equipements_detectes.length - 10} autre(s)</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Print-only header */}
      <div className="hidden print:block" style={{ marginBottom: 20, paddingBottom: 15, borderBottom: '2px solid #111' }}>
        <h1 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 4 }}>BON DE TRAVAIL</h1>
        <p style={{ fontSize: 14, color: '#555' }}>{dossier?.client_nom} — {bon.type_travaux === 'interieur' ? 'Intérieur' : 'Extérieur'}</p>
        <p style={{ fontSize: 12, color: '#888' }}>Peinture Résidentielle QC</p>
        {bon.heures_budgetees > 0 && (
          <p style={{ fontSize: 13, color: '#333', marginTop: 4, fontWeight: '600' }}>
            Heures budgétées : {bon.heures_budgetees}h
            {bon.heure_debut && ` · Début : ${bon.heure_debut}`}
            {bon.chef_equipe_nom && ` · Chef : ${bon.chef_equipe_nom}`}
          </p>
        )}
      </div>

      {/* Header */}
      <div className="flex justify-between items-center no-print">
        <Button variant="ghost" onClick={onBack}>&larr; Retour à la liste</Button>
        <div className="flex gap-2 flex-wrap">
          {/* 1. Modifier */}
          {(!canDoBonTravailAction || canDoBonTravailAction('bon_modifier')) && (
          <Button variant="outline" size="sm" className="flex" onClick={onEdit} data-testid="btn-modifier-bon">
            <ClipboardList className="w-4 h-4 mr-1" /> Modifier
          </Button>
          )}
          {/* 2. Courriel présentation — RBAC `bon_envoyer_presentation` (Vague 5b) */}
          {canEnvoyerPresentation && (bon.produits?.length > 0 || bon.produits_fournis_par_client) && (
            <Button variant="outline" size="sm" onClick={handleOpenPresentation} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="btn-envoyer-presentation">
              <Send className="w-4 h-4 mr-1" /> Courriel présentation
            </Button>
          )}
          {/* 3. Travaux terminés (with equipment + étapes status) */}
          {/* 🆕 2026-05 — Permission `bon_travaux_termines` doit débloquer le
               bouton pour adjoint admin / chargée projet, sans imposer canManage. */}
          {!bon.termine && (canManage || (canDoBonTravailAction && canDoBonTravailAction('bon_travaux_termines'))) && (
            <BtnTravauxTermines
              bonId={bon.id}
              equipements={bon.equipements}
              etapes={bon.etapes}
              onDone={() => { if (onRefresh) onRefresh(); }}
            />
          )}
          {/* Badge "Terminé" visible une fois les travaux complétés */}
          {bon.termine && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium border border-emerald-200 no-print">
              <CheckCircle className="w-4 h-4" /> Travaux terminés
            </span>
          )}
          {/* 🆕 [v13.59] Bouton « Décocher terminé » — permission dynamique */}
          {bon.termine && (
            <BtnDecocherTravauxTermines
              bonId={bon.id}
              onDone={() => { if (onRefresh) onRefresh(); }}
            />
          )}
          {/* 4. Créer Service Call — uniquement si travaux terminés */}
          {(!canDoBonTravailAction || canDoBonTravailAction('bon_creer_service_call')) && (
          <div className="flex items-center gap-1 no-print">
            <Button
              variant="outline"
              size="sm"
              onClick={() => bon.termine ? setShowServiceCallDialog(true) : toast.error('Les travaux doivent être marqués comme terminés avant de créer un service call.')}
              disabled={!bon.termine}
              className={bon.termine ? 'text-orange-600 border-orange-200 hover:bg-orange-50' : 'text-gray-400 border-gray-200 cursor-not-allowed'}
              data-testid="btn-creer-service-call"
              title={!bon.termine ? 'Travaux non terminés' : ''}
            >
              <Wrench className="w-4 h-4 mr-1" /> Créer Service Call
            </Button>
          </div>
          )}
          {/* 5. Visualiser en PDF (Phase 63) — rendu pixel-perfect côté backend via Playwright */}
          {canImprimer && (
            <Button variant="outline" size="sm" className="hidden sm:flex" onClick={async () => {
              try {
                const res = await axios.get(`${API_URL}/api/pdf/bon-travail/${bon.id}`, { responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                // Note : URL.revokeObjectURL retardé pour que l'onglet ait le temps de charger
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (e) {
                toast.error("Impossible de générer le PDF du bon de travail.");
              }
            }} data-testid="btn-imprimer-bon">
              <Printer className="w-4 h-4 mr-1" /> Visualiser en PDF
            </Button>
          )}
          {/* Lier Dossier (Jumelé) — RBAC `bon_lier_dossier` (Vague 5b) */}
          {canLierDossier && (
            <Button variant="outline" size="sm" className="hidden sm:flex text-purple-600 border-purple-200 hover:bg-purple-50" onClick={() => setShowLierDossier(true)} data-testid="btn-lier-dossier">
              <Building2 className="w-4 h-4 mr-1" /> Lier Dossier
            </Button>
          )}
          {/* 6. Supprimer */}
          {canSupprimerDetail && (
            <Button variant="outline" size="sm" className="hidden sm:flex text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete} data-testid="btn-supprimer-bon">
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* ─── Bannière Heures Budgétées / Réelles / Restantes ─── */}
      <HeuresBannerBon bon={bon} />

      {/* Informations Client & Dossier */}
      <Card className="print-card print-no-break bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Infos Client */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> Informations Client
              </h3>
              <div className="space-y-1.5">
                <p className="font-semibold text-gray-900">{dossier?.client_nom || 'Client non défini'}</p>
                {clientInfo?.telephone && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    <a href={`tel:${clientInfo.telephone}`} className="hover:text-blue-600">{clientInfo.telephone}</a>
                  </p>
                )}
                {clientInfo?.email && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <a href={`mailto:${clientInfo.email}`} className="hover:text-blue-600">{clientInfo.email}</a>
                  </p>
                )}
                {clientInfo?.adresse_client && (
                  hideAddress || (bon.route_lavage_id && !bon.route_terminee) ? (
                    <p className="text-sm text-cyan-700 flex items-center gap-1.5 italic bg-cyan-50 border border-dashed border-cyan-200 rounded-md px-2 py-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      <span>Adresse masquée — visible au démarrage de la route</span>
                    </p>
                  ) : (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5 flex-wrap">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{clientInfo.adresse_client}, {clientInfo.ville_client} {clientInfo.code_postal_client}</span>
                    <GoogleMapsBtn address={clientInfo.adresse_client} ville={clientInfo.ville_client} codePostal={clientInfo.code_postal_client} />
                  </p>
                  )
                )}
              </div>
            </div>

            {/* Infos Dossier */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> Informations Projet
              </h3>
              <div className="space-y-1.5">
                <p className="font-semibold text-gray-900">{dossier?.nom_dossier || 'Projet non défini'}</p>
                <p className="text-sm text-gray-600">
                  <span className="text-gray-500">N° Dossier:</span> {dossier?.numero_dossier || '-'}
                </p>
                {dossier?.adresse_travaux && !hideAddress && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5 flex-wrap">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{dossier.adresse_travaux}, {dossier.ville_travaux} {dossier.code_postal_travaux}</span>
                    <GoogleMapsBtn address={dossier.adresse_travaux} ville={dossier.ville_travaux} codePostal={dossier.code_postal_travaux} />
                  </p>
                )}
                {dossier?.representant_nom && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500">Représentant:</span> {dossier.representant_nom}
                    {teamInfo[dossier.representant_id]?.telephone && (
                      <a href={`tel:${teamInfo[dossier.representant_id].telephone}`} className="hover:text-blue-600 ml-1">
                        <Phone className="w-3 h-3 inline" /> {teamInfo[dossier.representant_id].telephone}
                      </a>
                    )}
                  </p>
                )}
                {dossier?.charge_projet_nom && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500">Chargée de projet:</span> {dossier.charge_projet_nom}
                    {teamInfo[dossier.charge_projet_id]?.telephone && (
                      <a href={`tel:${teamInfo[dossier.charge_projet_id].telephone}`} className="hover:text-blue-600 ml-1">
                        <Phone className="w-3 h-3 inline" /> {teamInfo[dossier.charge_projet_id].telephone}
                      </a>
                    )}
                  </p>
                )}
                {(bon?.chef_equipe_nom || dossier?.chef_equipe_nom) && (
                  <p className="text-sm text-gray-600 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500">Chef d'équipe:</span> {bon?.chef_equipe_nom || dossier?.chef_equipe_nom}
                    {teamInfo[bon?.chef_equipe_id || dossier?.chef_equipe_id]?.telephone && (
                      <a href={`tel:${teamInfo[bon?.chef_equipe_id || dossier?.chef_equipe_id].telephone}`} className="hover:text-blue-600 ml-1">
                        <Phone className="w-3 h-3 inline" /> {teamInfo[bon?.chef_equipe_id || dossier?.chef_equipe_id].telephone}
                      </a>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Clients liés (Jumelé) — RBAC `bon_voir_dossiers_lies_montants` (Vague 5b) */}
          {bon.dossiers_lies?.length > 1 && canVoirDossiersLiesMontants && bon.dossiers_lies.filter(d => d.dossier_id !== bon.dossier_id).map((dl, i) => (
            <div key={dl.id || dl._id || i} className="border-t pt-4 mt-4">
              <h3 className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> Client lié — {dl.client_nom} ({dl.prorata_pct}%)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{dl.client_nom}</p>
                  <p className="text-sm text-slate-500">{dl.montant_ht?.toLocaleString('fr-CA', {style:'currency',currency:'CAD'})} (sans taxes)</p>
                </div>
                <div>
                  <a href={`/crm/projets/${dl.dossier_id}`} className="text-sm text-blue-600 hover:underline">Voir le dossier →</a>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      {/* Dossiers liés — RBAC `bon_voir_dossiers_lies_section` (Vague 5b) */}
      {bon.dossiers_lies?.length > 1 && canVoirDossiersLiesSection && (
        <Card className="print-card border-purple-200">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-purple-500" /> Dossiers liés ({bon.dossiers_lies.length} dossiers — Jumelé)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="space-y-2">
              {bon.dossiers_lies.map((d, i) => (
                  <div key={d.id || d._id || i} className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                    <div>
                      <span className="font-semibold text-purple-800">{d.client_nom}</span>
                      <span className="text-xs text-purple-500 ml-2">{d.montant_ht?.toLocaleString('fr-CA', {style:'currency',currency:'CAD'})} (sans taxes)</span>
                    </div>
                    <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">{d.prorata_pct}%</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-semibold text-purple-800 pt-1 border-t border-purple-200">
                  <span>Total combiné</span>
                  <span>{bon.dossiers_lies.reduce((s, d) => s + (d.montant_ht || 0), 0).toLocaleString('fr-CA', {style:'currency',currency:'CAD'})} (sans taxes)</span>
                </div>
              </div>
          </CardContent>
        </Card>
      )}

      {/* 🆕 [Phase 41 BIS] Encadré stats agrégées BT liés */}
      {statsAgregees?.is_aggregated && (
        <Card className="border-indigo-300 bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="card-stats-agregees">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-indigo-800">
              <span className="text-base">🔗</span> Stats agrégées du groupe de BT liés
              <span className="text-[11px] font-normal text-indigo-600 ml-1" title="Les valeurs ci-dessous somment toutes les BT du groupe (montant, heures, produits, matériaux)">
                ⓘ
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 space-y-3">
            <div>
              <p className="text-[11px] uppercase font-semibold text-indigo-700 mb-1.5">
                BT inclus dans l'agrégation ({statsAgregees.bts_inclus?.length || 0})
              </p>
              <div className="space-y-1">
                {(statsAgregees.bts_inclus || []).map(b => (
                  <div
                    key={b.id}
                    className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${b.id === bon.id ? 'bg-indigo-200 font-semibold' : 'bg-white border border-indigo-200'}`}
                    data-testid={`stats-agregees-bt-${b.id}`}
                  >
                    <span className="text-indigo-900">
                      {b.numero_bt || '—'}{b.nom_dossier ? ` · ${b.nom_dossier}` : ''}
                      {b.id === bon.id && <span className="ml-2 text-[10px] text-indigo-500">(ce BT)</span>}
                    </span>
                    <span className="text-indigo-600 text-[11px]">{b.client_nom || ''}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-indigo-200">
              <div className="bg-white rounded p-2 border border-indigo-100" data-testid="stats-agreg-montant">
                <p className="text-[10px] text-indigo-600 uppercase font-semibold">Montant HT</p>
                <p className="text-sm font-bold text-indigo-900">
                  {(statsAgregees.totaux?.montant_ht || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                </p>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-100" data-testid="stats-agreg-heures-bud">
                <p className="text-[10px] text-indigo-600 uppercase font-semibold">Heures bud.</p>
                <p className="text-sm font-bold text-indigo-900">{statsAgregees.totaux?.heures_budgetees || 0}h</p>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-100" data-testid="stats-agreg-heures-reelles">
                <p className="text-[10px] text-indigo-600 uppercase font-semibold">Heures réelles</p>
                <p className="text-sm font-bold text-indigo-900">{statsAgregees.totaux?.heures_reelles || 0}h</p>
              </div>
              <div className="bg-white rounded p-2 border border-indigo-100" data-testid="stats-agreg-prod-mat">
                <p className="text-[10px] text-indigo-600 uppercase font-semibold">Prod. / Mat.</p>
                <p className="text-sm font-bold text-indigo-900">
                  {statsAgregees.totaux?.nb_produits || 0} / {statsAgregees.totaux?.nb_materiaux || 0}
                </p>
              </div>
            </div>
            {/* 💰 Bonus : Rentabilité réelle du groupe (basée sur coût employeur via ccq_snapshot) */}
            {(statsAgregees.totaux?.heures_reelles || 0) > 0 && (statsAgregees.totaux?.montant_ht || 0) > 0 && (() => {
              // 🔧 [Phase 42 #1] Utilise le coût employeur RÉEL agrégé via ccq_snapshot
              // sur chaque punch. Plus de hardcode 35$/h.
              const cout_mo = statsAgregees.totaux.cout_mo_reel || 0;
              const marge = statsAgregees.totaux.marge_reelle ?? (statsAgregees.totaux.montant_ht - cout_mo);
              const marge_pct = statsAgregees.totaux.marge_pct ?? ((marge / statsAgregees.totaux.montant_ht) * 100);
              const isPositive = marge >= 0;
              const isHealthy = marge_pct >= 30;
              const taux_moyen = (statsAgregees.totaux.heures_reelles || 0) > 0
                ? cout_mo / statsAgregees.totaux.heures_reelles
                : 0;
              return (
                <div className="pt-2 border-t border-indigo-200" data-testid="stats-agreg-rentabilite">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] uppercase font-semibold text-indigo-700">💰 Rentabilité réelle</p>
                    <p className={`text-xs font-bold ${isHealthy ? 'text-emerald-700' : isPositive ? 'text-amber-700' : 'text-red-700'}`}>
                      {marge.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })} ({marge_pct.toFixed(0)}%)
                    </p>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-1.5 ${isHealthy ? 'bg-emerald-500' : isPositive ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.max(0, Math.min(100, marge_pct))}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Coût MO réel : {cout_mo.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                    {taux_moyen > 0 && ` (taux moyen ${taux_moyen.toFixed(2)}$/h via ccq_snapshot des punches)`}
                  </p>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Dialog Lier un dossier */}
      {showLierDossier && (
        <Dialog open={showLierDossier} onOpenChange={setShowLierDossier}>
          <DialogContent>
            <DialogHeader><DialogTitle>Lier un autre dossier (Jumelé)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Recherchez le dossier client à lier à ce bon de travail. Les heures seront recalculées sur le total combiné.</p>
              <input
                type="text"
                value={searchLierDossier}
                onChange={e => setSearchLierDossier(e.target.value)}
                placeholder="Rechercher par nom de client..."
                className="w-full border rounded-lg px-3 py-2 text-sm"
                data-testid="search-lier-dossier"
              />
              <div className="max-h-60 overflow-y-auto space-y-1">
                {dossierSearchResults.filter(d => d.id !== bon.dossier_id && !bon.dossiers_lies?.find(dl => dl.dossier_id === d.id)).map(d => (
                  <button key={d.id} onClick={async () => {
                    try {
                      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
                      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/lier-dossier`, 
                        { dossier_id: d.id },
                        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
                      );
                      const result = res.data;
                      if (res.status === 200) {
                        toast.success(result.message);
                        setShowLierDossier(false);
                        if (onRefresh) onRefresh();
                      } else toast.error(result.detail || 'Erreur');
                    } catch { toast.error('Erreur réseau'); }
                  }} className="w-full text-left p-2 hover:bg-purple-50 rounded border text-sm">
                    <span className="font-medium">{d.client_nom}</span>
                    <span className="text-xs text-slate-400 ml-2">{d.numero_dossier} — {d.departement}</span>
                  </button>
                ))}
                {searchLierDossier.length > 0 && dossierSearchResults.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">Aucun dossier trouvé</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Équipe du chantier — toujours visible */}
      <Card className="print-card border-blue-100" data-testid="section-equipe-chantier">
        <CardHeader className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <CardTitle className="text-sm flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500 shrink-0" /> Équipe du chantier
          </CardTitle>
          {canManage && (!canDoBonTravailAction || canDoBonTravailAction('bon_ajouter_equipe')) && (
            <div className="text-xs text-slate-500 italic no-print">
              Utilisez "Modifier" pour changer l'équipe
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-0 pb-3 space-y-2">
          {/* Chef d'équipe */}
          {bon.chef_equipe_nom && (
            <div className="flex flex-wrap items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
              <span className="text-sm font-semibold truncate">{bon.chef_equipe_nom}</span>
              <span className="text-xs text-orange-600 bg-orange-100 px-1.5 rounded-full shrink-0">Chef</span>
              {teamInfo[bon.chef_equipe_id]?.telephone && (
                <a
                  href={`tel:${teamInfo[bon.chef_equipe_id].telephone}`}
                  className="ml-auto flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs shrink-0 whitespace-nowrap"
                >
                  <Phone className="w-3 h-3 shrink-0" />
                  <span className="truncate max-w-[110px] sm:max-w-none">{teamInfo[bon.chef_equipe_id].telephone}</span>
                </a>
              )}
            </div>
          )}
          {/* Membres de son équipe — depuis employes_assignes OU depuis l'équipe actuelle du chef */}
          {(() => {
            const hasAssignedData = Array.isArray(bon.employes_assignes) && bon.employes_assignes.length > 0;
            const membresAssignes = hasAssignedData
              ? bon.employes_assignes.filter(e => e.employee_id !== bon.chef_equipe_id && e.role !== 'chef_equipe' && !e.est_chef)  // E3b-whitelist : rôle d'assignation sur le BT (donnée)
              : [];
            const membresFallback = !hasAssignedData
              ? (employees || []).filter(e => e.chef_equipe_id === bon.chef_equipe_id && e.est_actif !== false)
              : [];
            const tousLesMembres = hasAssignedData ? membresAssignes : membresFallback.map(e => ({
              employee_id: e.id, employee_nom: `${e.prenom} ${e.nom}`, nom: `${e.prenom} ${e.nom}`
            }));
            return tousLesMembres.map((emp, i) => {
              const fullEmp = localEmployees?.find(e => e.id === emp.employee_id);
              const nomAffiche = emp.employee_nom || emp.nom || (fullEmp ? `${fullEmp.prenom} ${fullEmp.nom}` : '—');
              const tel = fullEmp?.telephone || emp.telephone;
              // 🗺️ [v13.63 Phase 3 — 2026-02-28] Badge « Sélection manuelle »
              //   Traçabilité : l'employé s'est auto-assigné via « Rechercher
              //   mon chantier ». Affiché ici pour laisser une trace admin.
              const selManuelle = emp.assignation_manuelle_employe === true;
              const selManuelleDate = emp.assignation_manuelle_at
                ? new Date(emp.assignation_manuelle_at).toLocaleDateString('fr-CA')
                : null;
              return (
                <div key={`emp-${i}`} className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                  <span className="text-sm font-medium truncate">{nomAffiche}</span>
                  {selManuelle && (
                    <Badge
                      variant="outline"
                      className="text-[10px] bg-violet-50 text-violet-700 border-violet-300 shrink-0"
                      title={selManuelleDate ? `Sélection manuelle par l'employé le ${selManuelleDate}` : "Sélection manuelle par l'employé"}
                      data-testid={`badge-selection-manuelle-${emp.employee_id}`}
                    >
                      🔎 Sélection manuelle{selManuelleDate ? ` — ${selManuelleDate}` : ''}
                    </Badge>
                  )}
                  {tel && (
                    <a
                      href={`tel:${tel}`}
                      className="ml-auto flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs shrink-0 whitespace-nowrap"
                    >
                      <Phone className="w-3 h-3 shrink-0" />
                      <span className="truncate max-w-[110px] sm:max-w-none">{tel}</span>
                    </a>
                  )}
                </div>
              );
            });
          })()}
          {!(bon.chef_equipe_nom) && !(bon.employes_assignes?.length > 0) && (
            <p className="text-xs text-slate-400 text-center py-2">Aucun employé assigné</p>
          )}
          {/* ✅ Section d'ajout rapide supprimée - Utiliser uniquement la page "Modifier" */}
        </CardContent>
      </Card>
      <Card className="print-card print-no-break">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className={`p-2.5 rounded-lg shrink-0 ${isExt ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
              <TypeIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-bold leading-tight">{isExt ? 'Bon — Extérieur' : 'Bon — Intérieur'}</h2>
                <Badge className={`shrink-0 no-print ${bon.statut === 'termine' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-testid="badge-statut-bon">{bon.statut === 'termine' ? 'Terminé' : bon.statut}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-gray-500 mt-1">
                {bon.chef_equipe_nom && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{bon.chef_equipe_nom}</span>}
                {bon.heures_budgetees > 0 && <span>{bon.heures_budgetees}h budgétées</span>}
                <span>{new Date(bon.date_creation).toLocaleDateString('fr-CA')}</span>
              </div>
            </div>
          </div>
          {/* Dates de chantier + Équipements + Inspection */}
          {/* 🐛 2026-05-09 — Filtrer les entrées nacelle de l'ancien format `equipements[]` :
              la nacelle est désormais affichée exclusivement par la carte dédiée plus bas. */}
          {(() => {
            const equipementsAffichables = (bon.equipements || []).filter(eq =>
              !((eq.equipement_nom || '').toLowerCase().includes('nacelle'))
            );
            return (bon.dates_chantier?.length > 0 || equipementsAffichables.length > 0) && (
            <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-xs">
              {bon.dates_chantier?.length > 0 && (
                <div>
                  <span className="text-gray-500 font-medium">Dates de chantier : </span>
                  <span className="text-gray-700">{bon.dates_chantier.map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })).join(', ')}</span>
                </div>
              )}
              {equipementsAffichables.length > 0 && (
                <div className="w-full">
                  <span className="text-gray-500 font-medium">Équipements : </span>
                  {equipementsAffichables.map((eq, i) => (
                    <div key={eq.id || eq._id || i} className="inline-block mr-3">
                      <span>{eq.emoji} {eq.equipement_nom}</span>
                      <span className="text-gray-500 ml-1">
                        ({eq.dates_utilisation?.map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })).join(', ') || 'Aucune date'})
                      </span>
                      {eq.utilisation_terminee && <Badge className="ml-1 bg-green-100 text-green-800 text-[10px]">Terminé</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })()}
          <div className="mt-3 pt-3 border-t" />
        </CardContent>
      </Card>

      {/* Description */}
      <Card className="print-card">
        <CardHeader><CardTitle className="text-base">Description des Travaux</CardTitle></CardHeader>
        <CardContent>
          {/* 🐛 2026-05-15 — Préserver INTÉGRALEMENT le formatage HTML (gras, taille,
              listes à puces, interlignes, paragraphes vides) saisi via TipTap.
              Auparavant `[&_*]:my-0` annulait les marges et écrasait l'apparence.
              On utilise désormais la classe `bt-riche` (styles inline ci-dessous)
              pour rendre fidèlement <h1>/<h2>/<h3>/<ul>/<ol>/<li>/<p>. */}
          <style>{`
            .bt-riche p { margin: 0 0 8px 0; min-height: 1em; }
            .bt-riche p:empty::before { content: '\\00a0'; }
            .bt-riche h1 { font-size: 20px; font-weight: 700; margin: 14px 0 8px 0; line-height: 1.3; }
            .bt-riche h2 { font-size: 17px; font-weight: 700; margin: 12px 0 6px 0; line-height: 1.3; }
            .bt-riche h3 { font-size: 15px; font-weight: 600; margin: 10px 0 6px 0; line-height: 1.3; }
            .bt-riche strong { font-weight: 700; }
            .bt-riche em { font-style: italic; }
            .bt-riche u { text-decoration: underline; }
            .bt-riche mark { background-color: #fef08a; padding: 0 2px; border-radius: 2px; }
            .bt-riche ul { padding-left: 24px; margin: 8px 0; list-style-type: disc; }
            .bt-riche ol { padding-left: 24px; margin: 8px 0; list-style-type: decimal; }
            .bt-riche li { margin: 4px 0; }
            .bt-riche blockquote { border-left: 3px solid #d1d5db; padding-left: 12px; margin: 8px 0; color: #4b5563; }
            .bt-riche.bt-riche-notes mark { background-color: #fde68a; }
          `}</style>
          <div
            className="bt-riche text-sm"
            style={{ lineHeight: '1.6' }}
            data-testid="bt-detail-description"
            dangerouslySetInnerHTML={{ __html: bon.description_travaux || '' }}
          />
          {bon.notes_speciales && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm font-medium text-amber-800 mb-1">Notes spéciales</p>
              <div
                className="bt-riche bt-riche-notes text-sm text-amber-900"
                style={{ lineHeight: '1.6' }}
                data-testid="bt-detail-notes-speciales"
                dangerouslySetInnerHTML={{ __html: bon.notes_speciales || '' }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* 🆕 2026-05-07 — Nouvelle carte Nacelle avec zones par jour
          (remplace l'ancien doublon dans bon.equipements[]) */}
      {isExt && bon.equipements_nacelle_requise && bon.equipements_nacelle_id && (() => {
        // Résoudre le nom de la nacelle depuis `bon.equipements[]` ou fallback
        const nacelleEntry = (bon.equipements || []).find(e =>
          e.equipement_id === bon.equipements_nacelle_id
          || (e.equipement_nom || '').toLowerCase().includes('nacelle')
        );
        // 🆕 [Phase 22 Bug 3] Affichage uniforme via helper formatNacelleDisplay :
        // priorité au snapshot stocké sur le BT (`equipements_nacelle_numero_unite`),
        // sinon on tente de récupérer depuis `bon.equipements[]`.
        const nacelleObj = {
          numero_unite: bon.equipements_nacelle_numero_unite || nacelleEntry?.numero_unite,
          nom: bon.equipements_nacelle_nom || nacelleEntry?.equipement_nom || nacelleEntry?.nom,
          modele: bon.equipements_nacelle_modele || nacelleEntry?.modele,
        };
        const nacelleLabel = formatNacelleDisplay(nacelleObj);
        const nacelleEmoji = nacelleEntry?.emoji || '🛗';
        const datesNacelle = bon.equipements_nacelle_dates || [];
        const zonesDuBon = bon.zones || [];
        const retourBureau = bon.equipements_nacelle_retour_type !== 'autre_chantier';
        const livraisonVeille = bon.equipements_nacelle_livraison_timing === 'veille';
        const utilisationTerminee = nacelleEntry?.utilisation_terminee === true;

        // Helper : nom de zone depuis numero
        const zoneLabel = (num) => {
          const z = zonesDuBon.find(zz => zz.numero === num);
          return z ? `Zone ${num}: ${z.nom}` : `Zone ${num}`;
        };

        return (
          <Card className="print-card border-cyan-200 bg-cyan-50/30" data-testid="card-nacelle-detail">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {nacelleLabel} — Plan d'utilisation
                {utilisationTerminee && (
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />Terminée
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Logistique de livraison */}
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="bg-white border-cyan-300 text-cyan-800">
                  🚚 Livraison : {livraisonVeille ? 'la veille' : 'le jour J'}
                </Badge>
                {bon.equipements_nacelle_vehicule_livraison_nom && (
                  <Badge variant="outline" className="bg-white border-cyan-300 text-cyan-800">
                    🚐 {bon.equipements_nacelle_vehicule_livraison_nom}
                  </Badge>
                )}
                <Badge variant="outline" className="bg-white border-cyan-300 text-cyan-800">
                  🔄 Retour : {retourBureau
                    ? 'au bureau'
                    : `chez ${bon.equipements_nacelle_client_livraison?.client_nom || 'autre chantier'}`}
                </Badge>
              </div>

              {/* Dates avec zones et notes */}
              {datesNacelle.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Aucune date d'utilisation définie.</p>
              ) : (
                <div className="space-y-2">
                  {datesNacelle.map((d, idx) => {
                    const zonesNums = d.zones_numeros || (d.zone_numero != null ? [d.zone_numero] : []);
                    return (
                      <div
                        key={d.date}
                        className="bg-white rounded-md border border-cyan-200 p-2.5"
                        data-testid={`nacelle-jour-${d.date}`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-medium text-sm text-slate-800">
                            📅 {new Date(d.date + 'T12:00:00').toLocaleDateString('fr-CA', {
                              weekday: 'long', day: 'numeric', month: 'long',
                            })}
                          </span>
                          {zonesNums.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {zonesNums.map(num => (
                                <Badge
                                  key={num}
                                  className="bg-cyan-100 text-cyan-900 border border-cyan-300 text-[10px]"
                                >
                                  {zoneLabel(num)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {idx === 0 && d.notes_positionnement && (
                          <div className="mt-1.5 pl-4 text-xs text-slate-600 border-l-2 border-amber-300">
                            💡 <span className="italic">{d.notes_positionnement}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions chef d'équipe (si la nacelle n'est pas encore terminée) */}
              {!utilisationTerminee && nacelleEntry && canManage
                && (!canDoBonTravailAction || canDoBonTravailAction('bon_equipements_location')) && (
                <div className="pt-2 border-t border-cyan-200">
                  <Button
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleEquipementTermine((bon.equipements || []).indexOf(nacelleEntry))}
                    data-testid="btn-nacelle-terminee"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Marquer l'utilisation comme terminée
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Équipements de location — anciens équipements (filtrés pour ne PAS dupliquer la nacelle) */}
      {isExt && (() => {
        // 🆕 2026-05-07 — Filtrer la nacelle si la nouvelle structure est utilisée,
        // pour éviter le doublon avec la carte "Plan d'utilisation" ci-dessus.
        const equipementsAffichables = (bon.equipements || []).filter(eq => {
          if (!bon.equipements_nacelle_requise) return true;
          // Exclure la nacelle gérée par le nouveau système
          if (eq.equipement_id && eq.equipement_id === bon.equipements_nacelle_id) return false;
          if ((eq.equipement_nom || '').toLowerCase().includes('nacelle')) return false;
          return true;
        });
        if (equipementsAffichables.length === 0) return null;
        const allTermines = equipementsAffichables.every(e => e.utilisation_terminee);

        return (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              🏗️ Équipements de location
              {allTermines && <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Tous terminés</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {equipementsAffichables.map((eq, eqIdx) => (
                <div key={eqIdx} className={`rounded-lg border p-3 ${eq.utilisation_terminee ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                  {/* Ligne info équipement */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{eq.emoji} {eq.equipement_nom}</p>
                      <p className="text-sm text-gray-600">
                        Dates prévues: {eq.dates_utilisation?.map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })).join(', ')}
                      </p>
                      {eq.utilisation_terminee && eq.date_fin_utilisation && (
                        <p className="text-xs text-green-600 mt-1">
                          Terminé le {new Date(eq.date_fin_utilisation).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    {eq.utilisation_terminee && (
                      <Badge className="bg-green-100 text-green-800 shrink-0"><CheckCircle className="w-4 h-4 mr-1" />Terminé</Badge>
                    )}
                  </div>

                  {/* Actions chef d'équipe */}
                  {!eq.utilisation_terminee && canManage && (!canDoBonTravailAction || canDoBonTravailAction('bon_equipements_location')) && (
                    <div className="mt-3 flex flex-col gap-2">
                      {/* Bouton terminé */}
                      <Button 
                        size="sm" 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => handleEquipementTermine(eqIdx)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Utilisation terminée
                      </Button>

                      {/* Ajouter du temps — compteur de jours */}
                      <div className="flex items-center gap-2 bg-white rounded-lg border p-2">
                        <span className="text-xs text-gray-600 flex-1">Ajouter du temps :</span>
                        <button
                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                          onClick={() => setProlongerDates(p => ({ ...p, [eq.equipement_id]: Math.max(1, (p[eq.equipement_id] || 1) - 1) }))}
                        >−</button>
                        <span className="w-8 text-center font-semibold text-sm">
                          {prolongerDates[eq.equipement_id] || 1} j.
                        </span>
                        <button
                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                          onClick={() => setProlongerDates(p => ({ ...p, [eq.equipement_id]: (p[eq.equipement_id] || 1) + 1 }))}
                        >+</button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-3 border-blue-400 text-blue-600 hover:bg-blue-50"
                          disabled={prolongerState?.[`${bon.id}_${eq.equipement_id}`]?.loading}
                          onClick={() => {
                            // Calculer la nouvelle date = dernière date prévue + N jours
                            const nbJours = prolongerDates[eq.equipement_id] || 1;
                            const dates = eq.dates_utilisation || [];
                            const lastDate = dates.length > 0
                              ? new Date(dates[dates.length - 1] + 'T12:00:00')
                              : new Date();
                            const newDate = new Date(lastDate);
                            newDate.setDate(newDate.getDate() + nbJours);
                            const dateStr = newDate.toISOString().split('T')[0];
                            onProlongerEquipement?.(bon.id, eq.equipement_id, dateStr);
                            setProlongerDates(p => ({ ...p, [eq.equipement_id]: 1 }));
                          }}
                          data-testid={`btn-prolonger-${eqIdx}`}
                        >
                          {prolongerState?.[`${bon.id}_${eq.equipement_id}`]?.loading ? '...' : 'Confirmer'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Lavage à pression — 🆕 2026-05-12 : carte toujours visible côté management pour permettre le toggle manuel
          (cas BT supprimé/recréé). Si lavage non activé sur le BT, affiche une carte légère avec bouton "Activer". */}
      {isExt && bon.lavage_pression_a_faire && (
        <Card className={`no-print ${bon.lavage_pression_complete ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`} data-testid="lavage-pression-section">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🚿</span>
                <div>
                  <p className="font-medium text-sm">
                    Lavage à pression {bon.lavage_pression_complete ? 'complété' : 'à faire'}
                  </p>
                  {bon.lavage_pression_complete && bon.lavage_pression_date_completion && (
                    <p className="text-xs text-green-700">
                      Complété le {new Date(bon.lavage_pression_date_completion).toLocaleDateString('fr-CA')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {!bon.lavage_pression_complete && canManage && (
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleLavagePressionComplete}
                    data-testid="lavage-complete-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Lavage complété
                  </Button>
                )}
                {bon.lavage_pression_complete && (
                  <>
                    <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-4 h-4 mr-1" />Complété</Badge>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => handleToggleLavage('complete', false, 'Lavage remis à « à faire »')}
                        data-testid="lavage-non-fait-btn"
                        title="Annuler la complétion — repasse en « à faire »"
                      >
                        Non fait
                      </Button>
                    )}
                  </>
                )}
                {canManage && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-600 hover:bg-slate-100"
                    onClick={() => handleToggleLavage('a_faire', false, 'Lavage désactivé sur ce BT')}
                    data-testid="lavage-desactiver-btn"
                    title="Désactiver complètement le lavage sur ce BT"
                  >
                    Désactiver
                  </Button>
                )}
              </div>
            </div>
            {/* Notes lavage à pression */}
            {bon.notes_lavage_pression && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs font-semibold text-blue-700 mb-1">Notes pour le lavage :</p>
                <p className="text-sm text-blue-900 bg-white/60 rounded-md px-3 py-2">{bon.notes_lavage_pression}</p>
              </div>
            )}
            {/* 🆕 Cases à cocher visibles pour tous (y compris employés lavage) */}
            {(bon.nacelle_lavage_requise || bon.echafauds_a_monter || bon.nettoyer_echelle) && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs font-semibold text-blue-700 mb-2">À faire pour le lavage :</p>
                <div className="space-y-1.5">
                  {bon.nacelle_lavage_requise && (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <CheckCircle className={`w-4 h-4 ${bon.nacelle_retour_termine_le ? 'text-green-600' : 'text-cyan-600'}`} />
                      <span className={bon.nacelle_retour_termine_le ? 'text-green-800 line-through opacity-70' : 'text-cyan-800'}>
                        🏗 Nacelle requise
                        {bon.nacelle_lavage_nom ? (
                          <> : <span className="font-semibold">{bon.nacelle_lavage_nom}</span></>
                        ) : null}
                      </span>
                      {bon.nacelle_retour_termine_le ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                          ✓ Terminée{bon.nacelle_retour_termine_par ? ` par ${bon.nacelle_retour_termine_par}` : ''}
                        </Badge>
                      ) : (
                        /* 🆕 [5.1.C] Bouton "Marquer la nacelle comme terminée" RETIRÉ d'ici.
                         * Le marquage passe désormais par le flow naturel : à la fin
                         * du dernier client de la route de lavage (Phase 5.5), un dialog
                         * demande à l'employé si la nacelle a été retournée. L'endpoint
                         * PATCH /api/crm/bons-travail/{id}/nacelle-termine reste actif
                         * et sera appelé depuis ce nouveau flow. */
                        <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 text-xs">
                          ⏳ En attente fin de lavage
                        </Badge>
                      )}
                    </div>
                  )}
                  {bon.echafauds_a_monter && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-orange-600" />
                        <span className="text-orange-800 font-medium">🔧 Échafauds à monter</span>
                      </div>
                      {/* Affichage multi-types */}
                      {bon.echafauds_utilises && bon.echafauds_utilises.length > 0 ? (
                        <div className="ml-6 flex flex-wrap gap-1.5 mt-0.5">
                          {bon.echafauds_utilises.map((echafaud, idx) => (
                            <Badge key={echafaud.id || echafaud._id || idx} variant="secondary" className="bg-orange-50 text-orange-700 border border-orange-200 text-xs">
                              {echafaud.type} {echafaud.dimensions} — {echafaud.quantite} étage{echafaud.quantite > 1 ? 's' : ''}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        // Fallback pour anciens BT (avant multi-types)
                        bon.type_etage_echafauds && bon.dimensions_etage_echafauds && (
                          <div className="ml-6">
                            <Badge variant="secondary" className="bg-orange-50 text-orange-700 border border-orange-200 text-xs">
                              {bon.type_etage_echafauds} {bon.dimensions_etage_echafauds} — {bon.nb_etages_utilises || 0} étage{(bon.nb_etages_utilises || 0) > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        )
                      )}
                    </div>
                  )}
                  {bon.nettoyer_echelle && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                      <span className="text-purple-800">
                        🪜 Nettoyer à l'échelle
                        {bon.nettoyer_echelle_type_pieds ? (
                          <> : <span className="font-semibold">Échelle {bon.nettoyer_echelle_type_pieds} pieds</span></>
                        ) : null}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 🆕 2026-05-12 — Carte "Activer Lavage" : visible si BT extérieur SANS lavage_pression_a_faire,
          côté management. Permet de réactiver le lavage si le BT précédent a été supprimé accidentellement
          et que le nouveau BT n'a jamais coché la case. */}
      {isExt && !bon.lavage_pression_a_faire && canManage && (
        <Card className="no-print border-slate-200 bg-slate-50" data-testid="lavage-pression-activer-section">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-2xl opacity-60">🚿</span>
                <div>
                  <p className="font-medium text-sm text-slate-700">Lavage à pression — non activé</p>
                  <p className="text-xs text-slate-500">Activez manuellement si le BT a été recréé après suppression.</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => handleToggleLavage('a_faire', true, 'Lavage activé sur ce BT')}
                data-testid="lavage-activer-btn"
              >
                Activer le lavage
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Produits */}
      {bon.produits_fournis_par_client && (
        <Card className="print-card" data-testid="card-produits-fournis-client">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-indigo-700">
              <Package className="w-4 h-4" /> Produits fournis par le client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-indigo-800 italic">
              Aucun produit à commander : le client fournit les matériaux pour ce bon de travail.
            </p>
          </CardContent>
        </Card>
      )}
      {!bon.produits_fournis_par_client && bon.produits?.length > 0 && (
        <Card className="print-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> Produits ({bon.produits.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm min-w-[480px]">
              <thead><tr className="border-b bg-gray-50">
                <th className="text-left p-2 font-medium">Produit</th>
                <th className="text-left p-2 font-medium">Fini</th>
                <th className="text-center p-2 font-medium">Unité</th>
                <th className="text-center p-2 font-medium">Qté prévue</th>
                <th className="text-left p-2 font-medium">{isExt ? 'Surface' : 'Pièce'}</th>
                <th className="text-left p-2 font-medium">Code couleur</th>
                <th className="text-center p-2 font-medium">Commandé</th>
              </tr></thead>
              <tbody>
                {bon.produits.map((p, i) => (
                  <tr key={p.id || p._id || i} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{p.produit_nom}</td>
                    <td className="p-2"><Badge variant="outline" className="text-xs">{p.fini || '—'}</Badge></td>
                    <td className="p-2 text-center capitalize">{p.unite}</td>
                    <td className="p-2 text-center">
                      {p.quantite_prevue > 0 ? (
                        <span className="font-semibold text-blue-700">{p.quantite_prevue}</span>
                      ) : (
                        <span className="text-red-400 text-xs">Non prévu</span>
                      )}
                    </td>
                    <td className="p-2 text-gray-600">{p.emplacement || '—'}</td>
                    <td className="p-2 text-gray-600">{p.code_couleur || '—'}</td>
                    <td className="p-2 text-center">
                      {p.commande_initiale_faite ?
                        <Badge className="bg-green-100 text-green-800 text-xs">Oui</Badge> :
                        <Badge className="bg-red-100 text-red-800 text-xs">Non</Badge>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Commander des produits — accessible à tous les rôles autorisés */}
      {(() => {
        const premiereCommandeVerrouillee = bon.premiere_commande_verrouillee;
        // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] RBAC `bon_commander_produits_pre_chantier_cp`
        //   remplace le hardcode CP/adjoint. 🔒 Cat H additionnel : `travail_commence` (état du BT)
        //   restreint dynamiquement — dès qu'un employé a punché, seuls chef+owner commandent.
        // 🆕 2026-02-20 — Règle métier :
        //   CP / Adjoint admin peuvent passer des commandes tant qu'aucun employé
        //   n'a punché sur ce BT (multi-commandes pré-chantier autorisées).
        //   Dès qu'un punch existe, seuls chef d'équipe + propriétaire commandent.
        const travailCommence = !!bon.travail_commence;
        const peutToujoursCommander = isChefEquipe || isProprietaire;
        const canCommander = peutToujoursCommander || (!travailCommence && canCommanderPreChantierCP);
        const showRestrictionMessage = travailCommence && canCommanderPreChantierCP && !peutToujoursCommander;
        if (!canCommander && !showRestrictionMessage) return null;
        if (canDoBonTravailAction && !canDoBonTravailAction('bon_commander_produits')) return null;
        return (
        <Card className="no-print" data-testid="card-commander-produits">
          <CardHeader>
            {/* ✅ Vue mobile : titre + sous-titre + bouton sur lignes séparées */}
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
              <div className="flex flex-col gap-1">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" /> Commander des produits
                </CardTitle>
                {(isChefEquipe || premiereCommandeVerrouillee) && (
                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 w-fit">
                    Commandes supplémentaires — Chef d'équipe
                  </Badge>
                )}
              </div>
              {canCommander && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => setShowCommander(!showCommander)}
                  data-testid="btn-toggle-commander"
                >
                  {showCommander ? 'Fermer' : 'Passer une commande'}
                </Button>
              )}
            </div>
          </CardHeader>
          {showRestrictionMessage && (
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="msg-restriction-commande">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Le chantier est commencé</p>
                  <p>Au moins un employé a punché sur ce bon de travail. Seul le <strong>chef d'équipe</strong> peut désormais passer les commandes supplémentaires (lien direct avec son bonus et le critère « Gallons avant 14h »).</p>
                </div>
              </div>
            </CardContent>
          )}
          {canCommander && showCommander && (
            <CardContent>
              <div className="space-y-4">
                {/* Message informatif */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium mb-1">Créez un historique de commande</p>
                    <p>Utilisez cette section pour passer une commande. Chaque commande sera enregistrée dans l'historique avec la date, l'heure et les produits commandés.</p>
                  </div>
                </div>

                {/* Produits assignés au bon */}
                {bon.produits?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Produits du bon de travail</p>
                    {bon.produits.map((p, i) => (
                      <div key={p.id || p._id || i} className={`flex items-center justify-between p-3 rounded-lg border mb-2 ${commandeForm[i]?.selected ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={commandeForm[i]?.selected || false}
                            onChange={(e) => setCommandeForm(f => ({ ...f, [i]: { ...f[i], selected: e.target.checked } }))}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-medium text-sm">{p.produit_nom}</p>
                            <p className="text-xs text-gray-500">
                              {p.fini} {p.emplacement ? `— ${p.emplacement}` : ''}
                              {p.quantite_prevue > 0 && <span className="ml-2 text-blue-600 font-medium">Prévision CP : {p.quantite_prevue} {p.unite}</span>}
                            </p>
                          </div>
                        </label>
                        {commandeForm[i]?.selected && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm">Qté:</span>
                            <Input
                              type="number" min="1" className="w-20 h-8"
                              value={commandeForm[i]?.quantite ?? ''}
                              placeholder="Qté"
                              onChange={(e) => {
                                const raw = e.target.value;
                                // 🆕 [Phase 65] Permet champ vide — pas de forçage à 1
                                setCommandeForm(f => ({ ...f, [i]: { ...f[i], quantite: raw === '' ? '' : Math.max(1, parseInt(raw) || 1) } }));
                              }}
                            />
                            <span className="text-sm text-gray-500">{p.unite}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Produits hors catalogue */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Ajouter hors catalogue</p>
                  {/* Barre de recherche */}
                  <div className="relative">
                    <Input
                      placeholder="Rechercher un produit du catalogue..."
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      className="pr-8"
                      data-testid="catalog-search-input"
                    />
                    {loadingCatalog && (
                      <div className="absolute right-2 top-2.5 w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    )}
                    {/* Résultats dropdown */}
                    {catalogSearch.trim().length >= 1 && (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {catalogProduits
                          .filter(p =>
                            (p.nom || '').toLowerCase().includes(catalogSearch.toLowerCase()) ||
                            (p.categorie || '').toLowerCase().includes(catalogSearch.toLowerCase())
                          )
                          .slice(0, 15)
                          .map(p => (
                            <button
                              key={p.id}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center text-sm border-b border-slate-50 last:border-0"
                              onClick={() => addExtraProduit(p)}
                              data-testid={`catalog-result-${p.id}`}
                            >
                              <span className="font-medium text-slate-800">{p.nom}</span>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                {p.en_stock != null && (
                                  <span className={`text-xs ${p.en_stock > 0 ? 'text-green-600' : 'text-red-400'}`}>
                                    {p.en_stock > 0 ? `${p.en_stock} en stock` : 'rupture'}
                                  </span>
                                )}
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${p.source === 'entrepot' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>
                                  {p.source === 'entrepot' ? 'Entrepôt' : 'Catalogue'}
                                </span>
                              </div>
                            </button>
                          ))
                        }
                        {catalogProduits.filter(p =>
                          (p.nom || '').toLowerCase().includes(catalogSearch.toLowerCase()) ||
                          (p.categorie || '').toLowerCase().includes(catalogSearch.toLowerCase())
                        ).length === 0 && (
                          <p className="px-3 py-2 text-sm text-slate-400">Aucun produit trouvé pour "{catalogSearch}"</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Produits hors catalogue sélectionnés */}
                  {extraProduits.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {extraProduits.map((p, i) => (
                        <div key={p.produit_id} className="flex items-center justify-between p-2 bg-violet-50 border border-violet-200 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-violet-800 truncate">{p.nom}</p>
                            {p.categorie && <p className="text-xs text-violet-500">{p.categorie}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <Input
                              type="number" min="0.5" step="0.5" className="w-20 h-7 text-sm"
                              value={p.quantite}
                              onChange={(e) => setExtraProduits(prev => prev.map((ep, ei) =>
                                ei === i ? { ...ep, quantite: parseFloat(e.target.value) || 1 } : ep
                              ))}
                            />
                            <span className="text-xs text-slate-500 w-8 shrink-0">{p.unite}</span>
                            <button
                              onClick={() => setExtraProduits(prev => prev.filter((_, ei) => ei !== i))}
                              className="text-red-400 hover:text-red-600"
                            >×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 🆕 2026-04-30 — Choix livraison/quand (CP + chef d'équipe) */}
                <div className="border-t pt-3 space-y-3" data-testid="livraison-questions">
                  {(isChefEquipe && !isProprietaire) ? (
                    <>
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">Quand voulez-vous recevoir votre commande ?</p>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => { setLivraisonQuand('aujourd_hui'); setLivraisonDestination('client'); }}
                            data-testid="btn-quand-aujourd-hui"
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonQuand === 'aujourd_hui' ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
                          >
                            🚚 Aujourd'hui
                          </button>
                          <button
                            type="button"
                            onClick={() => { setLivraisonQuand('demain'); setLivraisonDestination(''); }}
                            data-testid="btn-quand-demain"
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonQuand === 'demain' ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
                          >
                            📅 Demain
                          </button>
                        </div>
                      </div>
                      {livraisonQuand === 'aujourd_hui' && (
                        <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded" data-testid="info-livraison-client-forcee">
                          ℹ️ « Aujourd'hui » signifie livraison directe <strong>chez le client</strong>.
                        </p>
                      )}
                      {livraisonQuand === 'demain' && (
                        <div>
                          <p className="text-sm font-semibold text-slate-700 mb-2">Où voulez-vous être livré ?</p>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => { setLivraisonDestination('entrepot'); setFournisseurId(''); setMagasinId(''); }}
                              data-testid="btn-destination-entrepot"
                              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'entrepot' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                            >
                              🏭 Livrer dans l'entrepôt
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLivraisonDestination('client'); setFournisseurId(''); setMagasinId(''); }}
                              data-testid="btn-destination-client"
                              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'client' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                            >
                              🏠 Livrer chez le client
                            </button>
                            {/* 🆕 [2026-06-11 v2] 3e mode : Ramassage fournisseur (générique) */}
                            <button
                              type="button"
                              onClick={() => setLivraisonDestination('fournisseur')}
                              data-testid="btn-destination-fournisseur"
                              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'fournisseur' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                            >
                              📦 Ramassage fournisseur
                            </button>
                          </div>
                          {/* Sous-sélecteur fournisseur générique + magasins éventuels */}
                          {livraisonDestination === 'fournisseur' && (
                            <FournisseurRamassagePicker
                              fournisseurs={fournisseursRamassage}
                              fournisseurId={fournisseurId}
                              setFournisseurId={(id) => { setFournisseurId(id); setMagasinId(''); }}
                              magasinId={magasinId}
                              setMagasinId={setMagasinId}
                              quand={ramassageQuand}
                              setQuand={setRamassageQuand}
                              testIdSuffix=""
                            />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Où voulez-vous livrer cette commande ?</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => { setLivraisonDestination('entrepot'); setFournisseurId(''); setMagasinId(''); }}
                          data-testid="btn-destination-entrepot"
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'entrepot' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                        >
                          🏭 Livrer dans l'entrepôt
                        </button>
                        <button
                          type="button"
                          onClick={() => { setLivraisonDestination('client'); setFournisseurId(''); setMagasinId(''); }}
                          data-testid="btn-destination-client"
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'client' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                        >
                          🏠 Livrer chez le client
                        </button>
                        {/* 🆕 [2026-06-11 v2] 3e mode : Ramassage fournisseur (générique) */}
                        <button
                          type="button"
                          onClick={() => setLivraisonDestination('fournisseur')}
                          data-testid="btn-destination-fournisseur"
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${livraisonDestination === 'fournisseur' ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                        >
                          📦 Ramassage fournisseur
                        </button>
                      </div>
                      {livraisonDestination === 'fournisseur' && (
                        <FournisseurRamassagePicker
                          fournisseurs={fournisseursRamassage}
                          fournisseurId={fournisseurId}
                          setFournisseurId={(id) => { setFournisseurId(id); setMagasinId(''); }}
                          magasinId={magasinId}
                          setMagasinId={setMagasinId}
                          testIdSuffix="-cp"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Footer commande */}
                <div className="flex justify-between items-center pt-3 border-t">
                  <p className="text-xs text-amber-600">
                    ⚠️ Les commandes après 14h impactent le critère "Gestion Contrat"
                  </p>
                  <Button onClick={handlePasserCommande} disabled={savingCommande} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-passer-commande">
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    {savingCommande ? 'Envoi...' : 'Commander'}
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      );
      })()}

      {/* Historique des commandes */}
      {bon.historique_commandes?.length > 0 && (
        <Card className="no-print">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <History className="w-4 h-4" /> Historique des commandes ({bon.historique_commandes.length})
              </span>
              {/* 🆕 [Phase v13.61 Vague 5b — 2026-08-25] RBAC `bt_verrouiller_commande` (matrice bon_travail_actions).
                  Fallback hardcodé retiré — canVerrouillerCommande couvre owner/CP/adjoint par défaut. */}
              {bon.historique_commandes.length > 0 && !bon.premiere_commande_verrouillee && canVerrouillerCommande && (
                <button
                  data-testid="btn-verrouiller-premiere-commande"
                  onClick={async () => {
                    if (!confirm('Verrouiller la première commande ? Les produits commandés ne pourront plus être modifiés.')) return;
                    try {
                      const r = await axios.patch(`${API_URL}/api/crm/bons-travail/${bon.id}/verrouiller-premiere-commande`);
                      if (r.status === 200) { toast.success('Première commande verrouillée'); if (onRefresh) onRefresh(); }
                      else toast.error('Erreur verrouillage');
                    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur réseau'); }
                  }}
                  className="flex items-center gap-1.5 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700"
                >
                  <Lock className="w-3 h-3" /> Verrouiller 1re commande
                </button>
              )}
              {bon.premiere_commande_verrouillee && (
                <Badge className="bg-emerald-100 text-emerald-800 text-xs flex items-center gap-1">
                  <Lock className="w-3 h-3" /> 1re commande verrouillée
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bon.historique_commandes.map((cmd, cmdIdx) => {
                const dateObj = new Date(cmd.date);
                const dateStr = dateObj.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' });
                const heureStr = dateObj.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }).replace(':', ' h ');
                const canAnnuler = !bon.premiere_commande_verrouillee && canDoBonTravailAction && canDoBonTravailAction('bon_commander_produits');
                return (
                  <div key={cmdIdx} className={`p-3 rounded-lg border ${cmd.est_premiere_commande ? 'bg-emerald-50 border-emerald-200' : cmd.apres_15h30 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
                    {/* Ligne 1 : Date + badge + bouton annuler */}
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{dateStr}, {heureStr}</span>
                        {cmd.est_premiere_commande && <Badge className="bg-emerald-600 text-white text-[10px]">1re commande</Badge>}
                        {(cmd.apres_14h || cmd.apres_15h30) && (
                          <Badge className="bg-red-100 text-red-800 text-[10px]">
                            Après 14h {cmd.heure_commande ? `(${cmd.heure_commande})` : ''}
                          </Badge>
                        )}
                        {/* 🆕 2026-04-30 — Badges livraison */}
                        {cmd.livraison_destination === 'entrepot' && (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`badge-livraison-entrepot-${cmdIdx}`}>
                            🏭 Entrepôt
                          </Badge>
                        )}
                        {cmd.livraison_destination === 'client' && (
                          <Badge className="bg-blue-100 text-blue-800 text-[10px]" data-testid={`badge-livraison-client-${cmdIdx}`}>
                            🏠 Chez le client
                          </Badge>
                        )}
                        {/* 🆕 [2026-06-11 v2] Badge Ramassage fournisseur (générique).
                            Supporte les nouveaux snapshots (livraison_destination='fournisseur'
                            avec `fournisseur_nom` / `magasin_nom`) ET les anciens snapshots
                            (livraison_destination='sherwin' avec `sherwin_magasin_*`). */}
                        {(cmd.livraison_destination === 'fournisseur' || cmd.livraison_destination === 'sherwin') && (() => {
                          const frNom = cmd.fournisseur_nom || (cmd.livraison_destination === 'sherwin' ? 'Sherwin-Williams' : '');
                          const magNom = cmd.magasin_nom || cmd.sherwin_magasin_nom || '';
                          const magAdr = cmd.magasin_adresse || cmd.sherwin_magasin_adresse || cmd.fournisseur_adresse || '';
                          if (!frNom) return null;
                          return (
                            <Badge className="bg-yellow-100 text-yellow-800 text-[10px]" data-testid={`badge-livraison-fournisseur-${cmdIdx}`} title={magAdr}>
                              📦 {frNom}{magNom ? ` — ${magNom}` : ''}
                            </Badge>
                          );
                        })()}
                        {cmd.livraison_quand === 'aujourd_hui' && (
                          <Badge className="bg-purple-100 text-purple-800 text-[10px]" data-testid={`badge-quand-aujourd-${cmdIdx}`}>
                            🌞 Aujourd'hui
                          </Badge>
                        )}
                        {cmd.livraison_quand === 'demain' && (
                          <Badge className="bg-cyan-100 text-cyan-800 text-[10px]" data-testid={`badge-quand-demain-${cmdIdx}`}>
                            🌅 Demain matin
                          </Badge>
                        )}
                        {/* 🆕 2026-04-30 — Statut d'approbation */}
                        {cmd.statut_approbation === 'en_attente_cp' && (
                          <Badge className="bg-orange-100 text-orange-800 text-[10px] font-semibold" data-testid={`badge-attente-cp-${cmdIdx}`}>
                            ⏳ En attente CP{cmd.approval_code ? ` (OK-${cmd.approval_code})` : ''}
                          </Badge>
                        )}
                        {cmd.statut_approbation === 'approuvee_par_cp' && (
                          <Badge className="bg-green-100 text-green-800 text-[10px]" data-testid={`badge-approuvee-cp-${cmdIdx}`}>
                            ✅ Approuvée{cmd.approuvee_par ? ` par ${cmd.approuvee_par}` : ''}{cmd.approuvee_via === 'sms' ? ' (SMS)' : ''}
                          </Badge>
                        )}
                        {cmd.statut_approbation === 'approuvee_auto' && (
                          <Badge className="bg-emerald-50 text-emerald-700 text-[10px]" data-testid={`badge-auto-approuvee-${cmdIdx}`}>
                            ✓ Auto-approuvée (1re)
                          </Badge>
                        )}
                      </div>
                      {/* 🆕 [Phase v13.61 Vague 5b — 2026-08-25] RBAC `bon_approuver_commande` (matrice bon_travail_actions).
                          🔒 Cat H additionnel : ownership CP-du-dossier reste imposé côté backend. */}
                      {cmd.statut_approbation === 'en_attente_cp' && canApprouverCommande && (
                        <button
                          onClick={async () => {
                            if (!confirm(`Approuver la commande du ${dateStr} ?\nLe chef d'équipe sera notifié.`)) return;
                            try {
                              await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/commandes/${cmdIdx}/approuver`);
                              toast.success('Commande approuvée ✅');
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Erreur approbation');
                            }
                          }}
                          className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 font-semibold"
                          data-testid={`btn-approuver-commande-${cmdIdx}`}
                        >
                          ✅ Approuver
                        </button>
                      )}
                      {canAnnuler && (
                        <button
                          onClick={async () => {
                            if (!confirm(`Annuler cette commande du ${dateStr} ?\nLes produits seront démarqués comme « commandés ».`)) return;
                            try {
                              await axios.delete(`${API_URL}/api/crm/bons-travail/${bon.id}/commandes/${cmdIdx}`);
                              toast.success('Commande annulée');
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Erreur annulation');
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-800 hover:underline"
                          data-testid={`btn-annuler-commande-${cmdIdx}`}
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                    {/* Ligne 2 : Auteur + audit trail CP notifiée */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                      <p className="text-xs text-slate-600">Par : <span className="font-medium">{cmd.commandee_par}</span></p>
                      {cmd.cp_notifiee === true && (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px] flex items-center gap-1" title={`CP notifiée le ${cmd.cp_notifiee_at ? new Date(cmd.cp_notifiee_at).toLocaleString('fr-CA') : '—'}${cmd.cp_email ? ` à ${cmd.cp_email}` : ''}`}>
                          <CheckCircle className="w-3 h-3" /> CP notifiée
                        </Badge>
                      )}
                      {cmd.cp_notifiee === false && (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px]" title="Aucune CP attachée au dossier ou envoi échoué">
                          CP non notifiée
                        </Badge>
                      )}
                    </div>
                    {/* Ligne 3+ : Produits en liste verticale */}
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {cmd.produits.map((p, pi) => (
                        <li key={pi} className="flex items-start gap-1">
                          <span className="text-slate-400">•</span>
                          <span>
                            {p.produit_nom || p.nom}
                            <span className="text-slate-500"> ({p.quantite_commandee} {p.unite})</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Étapes (cochables) */}
      {bon.etapes?.length > 0 && (
        <Card className="print-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Étapes ({etapesCompleted}/{bon.etapes.length})</span>
              {etapesCompleted === bon.etapes.length && <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Toutes complétées</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {bon.etapes.map((etape, idx) => (
                <div key={etape.id || etape._id || idx} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${etape.completee ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-gray-50'}`}>
                  <Checkbox checked={etape.completee} onCheckedChange={() => onToggleEtape(idx)} disabled={!isChefEquipe} data-testid={`etape-check-${idx}`} />
                  <span className={`bg-gray-200 text-gray-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0 ${etape.completee ? 'bg-green-200 text-green-700' : ''}`}>{etape.numero}</span>
                  <div className="flex-1">
                    <p className={`text-sm ${etape.completee ? 'line-through text-gray-400' : ''}`}>
                      <DescriptionWithZones text={etape.description} />
                    </p>
                    {etape.completee && etape.date_completion && (
                      <p className="text-xs text-green-600 mt-1">Complétée le {new Date(etape.date_completion).toLocaleDateString('fr-CA')}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zones (extérieur) — toujours visible pour les bons extérieurs */}
      {isExt && (
        <Card className="border-green-200 no-print">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-green-600" /> Zones à Travailler {zones.length > 0 && `(${zones.length})`}</CardTitle>
              {zones.length === 0 && (!canDoBonTravailAction || canDoBonTravailAction('bon_ajouter_zones')) && (
                <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50" onClick={onEdit}>
                  <Plus className="w-3 h-3 mr-1" /> Ajouter zones
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {zones.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-green-200" />
                <p className="text-sm">Aucune zone définie</p>
                <p className="text-xs mt-1">Cliquez sur <strong>Modifier</strong> pour ajouter des zones et leurs photos</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 print:grid-cols-2 print:gap-2">
                {zones.map((zone) => (
                  <div key={zone.numero} className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow print:cursor-default print:hover:shadow-none print:break-inside-avoid" onClick={() => setZonePreview(zone)}>
                    {zone.photo_url ? (
                      <img src={urlFichier(zone.photo_url)} alt={`Zone ${zone.numero}`} className="w-full h-32 object-cover print:h-40" />
                    ) : (
                      <div className="w-full h-32 bg-gray-100 flex items-center justify-center print:hidden"><ImageIcon className="w-8 h-8 text-gray-300" /></div>
                    )}
                    <div className="p-2">
                      <div className="flex items-center gap-1.5">
                        <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold print:bg-green-700">Z{zone.numero}</span>
                        <span className="text-sm font-medium truncate print:whitespace-normal print:overflow-visible">{zone.nom || `Zone ${zone.numero}`}</span>
                      </div>
                      {zone.description && (
                        <p
                          className="text-xs text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap print:line-clamp-none print:text-[11px] print:text-black"
                          title={zone.description}
                        >
                          {zone.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photos du chantier (intérieur) — galerie simple */}
      {!isExt && (
        <Card className="border-blue-200 no-print" data-testid="section-photos-interieur">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-blue-600" /> Photos du chantier {btPhotos.length > 0 && `(${btPhotos.length})`}
              </CardTitle>
              {canAjouterPhotos && (
                <label className="cursor-pointer">
                  <input type="file" multiple accept="image/*" className="hidden"
                    onChange={e => handleUploadBtPhotos(Array.from(e.target.files))}
                    disabled={uploadingPhotos} />
                  <Button variant="outline" size="sm" className="text-blue-700 border-blue-300 hover:bg-blue-50 pointer-events-none"
                    data-testid="btn-upload-photos-interieur">
                    <Upload className="w-3 h-3 mr-1" /> {uploadingPhotos ? `Upload ${uploadProgress}%` : 'Ajouter photos'}
                  </Button>
                </label>
              )}
              {uploadingPhotos && uploadProgress > 0 && (
                <div className="ml-3 flex-1 max-w-xs">
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {btPhotos.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 text-blue-200" />
                <p className="text-sm">Aucune photo</p>
                <p className="text-xs mt-1">Ajoutez des photos du chantier pour l'équipe</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {btPhotos.map((photo, idx) => (
                  <div key={photo.id || photo._id || idx} className="relative group border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setPhotoPreview(photo)}>
                    <img src={urlFichier(photo.url)} alt={photo.filename || `Photo ${idx + 1}`} className="w-full h-32 object-cover" />
                    {(!canDoBonTravailAction || canDoBonTravailAction('bt_supprimer_photos')) && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDeleteBtPhoto(idx); }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`btn-delete-photo-${idx}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-xs px-2 py-1 truncate">
                      {photo.filename || `Photo ${idx + 1}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photo preview modal */}
      {photoPreview && (
        <Dialog open={!!photoPreview} onOpenChange={() => setPhotoPreview(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{photoPreview.filename || 'Photo'}</DialogTitle>
            </DialogHeader>
            <img src={urlFichier(photoPreview.url)} alt={photoPreview.filename || 'Photo'} className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      )}

      {/* Zone preview modal */}
      {zonePreview && (
        <Dialog open={!!zonePreview} onOpenChange={() => setZonePreview(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">Z{zonePreview.numero}</span>
                {zonePreview.nom || `Zone ${zonePreview.numero}`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {zonePreview.photo_url ? (
                <img
                  src={zonePreview.photo_url.startsWith('http') ? zonePreview.photo_url : `${API_URL}${zonePreview.photo_url}`}
                  alt={`Zone ${zonePreview.numero}`}
                  className="w-full rounded-lg"
                  data-testid="zone-preview-photo"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <p className="text-gray-400">Aucune photo pour cette zone</p>
                </div>
              )}
              {/* 🆕 2026-05-08 — Détails de la zone affichés dans l'aperçu */}
              {zonePreview.description && zonePreview.description.trim() && (
                <div className="p-3 bg-slate-50 border rounded-lg" data-testid="zone-preview-description">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Détails de la zone</p>
                  <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">
                    {htmlToPlainText(zonePreview.description)}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Section Inspection : visible si l'utilisateur a le droit d'agir OU simplement de voir */}
      {(canInspection || canInspectionVoir) && (
        <div className="no-print">
          <SectionInspection bon={bon} canEdit={canInspection} />
        </div>
      )}

      {/* Section Rapports d'Incidence */}
      {canSignalerIncident && (
        <div className="no-print mt-2">
          <div className="border border-orange-200 rounded-lg">
            <OngletRapportIncident bon={bon} />
          </div>
        </div>
      )}

      {/* Section Gestion du Contrat */}
      <div className="no-print">
        <SectionGestionContrat bon={bon} />
      </div>

      {/* Dialog Envoi Présentation */}
      <Dialog open={showPresentationModal} onOpenChange={setShowPresentationModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5" /> Envoyer la Présentation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {presentationTemplates.length > 0 && (
              <div>
                <Label>Choisir un modèle</Label>
                <Select onValueChange={(val) => {
                  const t = presentationTemplates.find(t => t.id === val);
                  if (t) handleSelectPresentationTemplate(t);
                }}>
                  <SelectTrigger data-testid="select-presentation-template"><SelectValue placeholder="Sélectionner un modèle..." /></SelectTrigger>
                  <SelectContent>
                    {presentationTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Email du client *</Label>
              <Input value={presentationData.email_client} onChange={(e) => setPresentationData(d => ({ ...d, email_client: e.target.value }))} placeholder="client@email.com" data-testid="presentation-email" />
            </div>
            <div>
              <Label>Sujet *</Label>
              <Input value={presentationData.sujet} onChange={(e) => setPresentationData(d => ({ ...d, sujet: e.target.value }))} placeholder="Présentation de vos travaux..." data-testid="presentation-sujet" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Message</Label>
                <div className="flex gap-1 text-xs">
                  <button type="button" className={`px-3 py-1 rounded-t border ${!presentationData._preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100'}`} onClick={() => setPresentationData(d => ({ ...d, _preview: false }))}>Modifier</button>
                  <button type="button" className={`px-3 py-1 rounded-t border ${presentationData._preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100'}`} onClick={() => { setPresentationData(d => ({ ...d, _preview: true })); fetchPreview(presentationData.contenu); }}>Aperçu</button>
                </div>
              </div>
              {presentationData._preview ? (
                loadingPreview ? (
                  <div className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                ) : (
                  <EmailPreviewFrame html={previewHtml} minHeight={360} />
                )
              ) : (
                <Textarea id="presentation-textarea" value={presentationData.contenu} onChange={(e) => setPresentationData(d => ({ ...d, contenu: e.target.value }))} rows={12} placeholder="Contenu du courriel..." data-testid="presentation-contenu" />
              )}
            </div>
            {/* Variables disponibles cliquables */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs font-semibold text-blue-900 mb-1">Variables disponibles <span className="font-normal text-blue-700">— Cliquez pour insérer</span></p>
              <div className="flex flex-wrap gap-1.5">
                {['client_nom', 'nom_dossier', 'numero_dossier', 'adresse_travaux', 'dates_chantier', 'chef_equipe', 'telephone_chef_equipe', 'charge_projet', 'telephone_charge_projet', 'representant', 'telephone_representant', 'tableau_produits', 'lien_codes_couleur', 'nom_entreprise', 'telephone_entreprise', 'adresse_entreprise', 'numero_rbq', 'avis_google', 'logo_email'].map(v => (
                  <button key={v} type="button" className="px-2 py-1 text-xs bg-white border rounded hover:bg-blue-100 transition-colors" onClick={() => {
                    const ta = document.getElementById('presentation-textarea');
                    if (ta) {
                      const start = ta.selectionStart;
                      const end = ta.selectionEnd;
                      const text = presentationData.contenu;
                      const newText = text.substring(0, start) + `{${v}}` + text.substring(end);
                      setPresentationData(d => ({ ...d, contenu: newText }));
                    } else {
                      setPresentationData(d => ({ ...d, contenu: d.contenu + `{${v}}` }));
                    }
                  }}>{`{${v}}`}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={presentationData.inclure_lien}
                onCheckedChange={(checked) => setPresentationData(d => ({ ...d, inclure_lien: !!checked }))}
                data-testid="presentation-inclure-lien"
              />
              <Label className="cursor-pointer text-sm">Inclure le lien pour saisie des codes couleur par le client</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowPresentationModal(false)}>Annuler</Button>
              <Button onClick={handleSendPresentation} disabled={sendingPresentation} className="bg-green-600 hover:bg-green-700" data-testid="btn-send-presentation">
                <Send className="w-4 h-4 mr-2" />
                {sendingPresentation ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog créer service call */}
      <CreerServiceCallDialog
        isOpen={showServiceCallDialog}
        onClose={() => setShowServiceCallDialog(false)}
        dossier={dossier}
        bon={bon}
        onCreated={onRefresh}
      />
    </div>
  );
/* ─── Dialog de création ─── */
}
